import moment from 'moment';
import {ActivityEndType} from '../backend/enums/activity-end-type';
import {AffiliationType} from '../backend/enums/affiliation-type';
import {BackendLanguageTag} from '../backend/enums/backend-language-tag';
import {CameraFacing} from '../backend/enums/camera-facing';
import {CaregiverRelationshipRole} from '../backend/enums/caregiver-relationship-role';
import {DeviceType} from '../backend/enums/device-type';
import {EntitySpecialPurpose} from '../backend/enums/entity-special-purpose';
import {EntityType} from '../backend/enums/entity-type';
import {InviteState} from '../backend/enums/invite-state';
import {PlaylistType} from '../backend/enums/playlist-type';
import {MobileNetworkType} from '../backend/enums/mobile-network-type';
import {OperativeSystemName} from '../backend/enums/operative-system-name';
import {ProcessStatus} from '../backend/enums/process-status';
import {RegionCode} from '../backend/enums/region-code';
import {RelationshipType} from '../backend/enums/relationship-type';
import {ReminderResponseValue} from '../backend/enums/reminder-response-value';
import {UserGender} from '../backend/enums/user-gender';
import {UserSignInType} from '../backend/enums/user-sign-in-type';
import {UserType} from '../backend/enums/user-type';
import {InviteContactMethod} from '../enums/invite-contact-method';
import {MomentLanguageCode} from '../enums/moment-language-code';
import {SignInError} from '../enums/sign-in-error';
import {TimeLapseType} from '../enums/time-lapse-type';
import {LanguageTranslation} from './types';
import {Religion} from '../backend/enums';
import {CaregiverTaskCompletion} from '../enums/caregiver-task-completion';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {RolePermissionType} from '../backend/enums/role-permission-type';
import {PosologySchedule} from '../backend/enums/posology-schedule';
import {Frequency} from '../types';
import {MaritalStatus} from '../backend/enums/marital-status';
import {EducationLevel} from '../backend/enums/education-level';

// Overriding
// https://github.com/moment/moment/blob/develop/locale/en-gb.js
moment.updateLocale(MomentLanguageCode.enGB, {
  longDateFormat: {
    LT: 'h:mm A',
    LTS: 'h:mm:ss A',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'D MMMM YYYY HH:mm',
    LLLL: 'dddd, D MMMM YYYY HH:mm',
  },
  // Start week on Sunday
  // https://github.com/react-dates/react-dates/issues/60#issuecomment-244576967
  week: {
    dow: 0,
  },
});

export const en_US: LanguageTranslation = {
  legal_documents: {
    terms_and_conditions: 'Terms and Conditions',
    privacy_policy: 'Privacy Policy',
  },
  products: {
    bluebird: 'Sioslife',
    bluebird_with_gender: 'to Sioslife',
  },
  family_applications: {
    title: 'Applications',
    entity: 'Entity',
    choose_entity: 'Choose an entity to view this senior’s applications.',
    no_affiliations: 'This senior has no entity affiliations. Applications are unavailable.',
    missing_affiliations:
      'This senior’s affiliations are unavailable. Please reload your session or contact support.',
    load_error:
      'Unable to load applications. Access may be denied or the application may be unavailable.',
    retry: 'Retry',
    more: 'Load more',
    back: 'Back to applications',
    access: 'Senior’s access',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable senior’s access',
    disable: 'Disable senior’s access',
    confirm:
      'Change this senior’s access to this application? This applies across entities; it does not install the app or change entity availability.',
    write_error: 'Could not change the senior’s access. Please try again.',
    write_success: 'Senior’s access updated.',
  },
  reminders: {
    group_time: 'Time',
    group_reminder: 'Reminder',
    group_repeats: 'Repeats',
    group_recipients: 'Recipients',
    group_actions: 'Actions',
    no_entity_assigned: 'No entity assigned',
    group_switch_entity: 'Switch to the reminder’s entity to manage it.',
    group_edit: 'Edit reminder group',
    group_delete: 'Delete reminder group',
    group_delete_confirm: 'Delete “{{title}}” for all recipients ({{count}})?',
    group_partial: 'Some changes failed. Retry failed changes or close to refresh.',
    group_future_required: 'Choose a future date/time.',
    group_advanced:
      'The time and repeat settings of this advanced schedule are fixed. You can change the start date.',
    my_title: 'My reminders',
    my_scope: 'Reminders you created across all entities.',
    creation_scope:
      'New reminders are created for seniors in the currently selected entity: {{entity}}.',
    recipient: 'Recipient',
    title: 'Reminders',
    subtitle: 'Manage reminders for this senior.',
    add: 'Create reminder',
    bulk_title: 'Create reminders',
    bulk_success: 'Created {{success}} of {{total}} reminders.',
    bulk_partial:
      'Created {{success}} of {{total}} reminders. Try again for the remaining seniors.',
    edit: 'Edit reminder',
    loading: 'Loading reminders…',
    empty_title: 'No reminders yet',
    empty_description: 'Create a reminder for the selected senior.',
    load_more: 'Load more',
    retry: 'Try again',
    load_error: 'Could not load reminders.',
    senior_unavailable: 'The selected senior is unavailable. Select the senior again to continue.',
    delete_title: 'Delete reminder',
    delete_description:
      'Are you sure you want to delete this reminder? This action cannot be undone.',
    delete: 'Delete',
    cancel: 'Cancel',
    enable: 'Enable reminder',
    disable: 'Disable reminder',
    status_active: 'Active',
    status_inactive: 'Disabled',
    next_run: 'Next scheduled time',
    save_error: 'Could not save the reminder.',
    save_success: 'Reminder saved successfully.',
    delete_error: 'Could not delete the reminder.',
    delete_success: 'Reminder deleted successfully.',
    active_error: 'Could not change the reminder status.',
    active_success: 'Reminder enabled successfully.',
    inactive_success: 'Reminder disabled successfully.',
    response_types: {yes_no: 'Yes or no', ok: 'OK'},
    recurrence: {
      once: 'Once',
      per_minute: 'Every minute',
      hourly: 'Every {{count}} hour(s)',
      daily: 'Every {{count}} day(s)',
      weekly: 'Every {{count}} week(s)',
      monthly: 'Every {{count}} month(s)',
      yearly: 'Every {{count}} year(s)',
      current_unsupported: 'Current value ({{value}})',
    },
    form: {
      title: 'Title',
      title_placeholder: 'Enter the reminder title',
      message: 'Message',
      message_placeholder: 'Enter the reminder message',
      response_type: 'Response type',
      date: 'Start date',
      time: 'Start time',
      recurrence: 'Repeat',
      recurrence_count: 'Repeat every',
      save: 'Save',
      title_required: 'A title is required.',
      title_too_long: 'The title cannot exceed {{max}} characters.',
      message_required: 'A message is required.',
      message_too_long: 'The message cannot exceed {{max}} characters.',
      date_required: 'A date is required.',
      time_required: 'A time is required.',
      invalid_date_time: 'Enter a valid date and time.',
      date_in_past: 'The date cannot be in the past.',
      time_in_past: 'The time cannot be in the past.',
    },
  },
  family_services: {
    title: 'Services',
    choose: 'Choose an institution',
    none: 'No affiliated institutions.',
    unavailable: 'Services are unavailable at your institutions.',
    empty: 'No services available.',
    error: 'Could not load services.',
    denied: 'Access denied.',
    not_found: 'Institution or service unavailable.',
    retry: 'Retry',
    call: 'Call',
    no_phone: 'Phone contact unavailable.',
    unsupported: 'Contact action unavailable for this service.',
    active: 'Active',
    inactive: 'Inactive',
    enable: 'Enable',
    disable: 'Disable',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save_error: 'Could not update the service.',
    saved: 'Service updated.',
    refresh_error: 'Updated, but could not refresh services.',
  },
  family_invite: {
    contact_method: 'Contact method',
    phone: 'Mobile phone',
    action: 'Invite a caregiver',
    context: 'Inviting a caregiver for {{name}}',
    name: 'Name',
    email: 'Email',
    next: 'Continue',
    back: 'Back',
    send: 'Send invitation',
    confirm: 'Send an invitation to {{name}} at {{destination}}?',
    success: 'Invitation sent. They must accept before becoming a contact.',
    uncertain:
      'The invitation may have been sent. Check with the recipient before sending another invitation.',
    invalid: 'Please check the name and contact details.',
    denied: 'You do not have permission to send this invitation.',
    unavailable: 'The selected senior is unavailable.',
  },
  family_profile: {
    user_details: 'User details',
    remove_link: 'Remove link',
    remove_link_success: 'Your relationship was removed.',
    remove_link_confirm:
      'Remove your relationship with {{name}}? You will lose access to their information. This will not delete the senior or anyone else’s relationship.',
    remove_link_error: 'Could not remove your relationship. Please try again.',
    remove_link_denied: 'You do not have permission to remove this relationship.',
    cancel: 'Cancel',
    institutions: 'Institutions',
    senior_contacts: 'Senior contacts',
    senior_empty: 'No senior contacts found.',
    title: 'Profile',
    contacts: 'Caregivers',
    empty: 'No caregivers found.',
    load_more: 'Load more',
    loading: 'Loading…',
    error: 'Could not load data.',
    denied: 'Access denied.',
    unavailable: 'This information is unavailable.',
    retry: 'Retry',
    close: 'Close',
  },
  family_media: {
    recording: 'Recording',
    camera: 'Use camera',
    take_photo: 'Take photo',
    record_video: 'Record video',
    stop_recording: 'Stop recording',
    camera_error: 'Camera or microphone unavailable. Check permissions and try again.',
    title: 'Family Album',
    download: 'Download',
    share: 'Share',
    export_error: 'Could not prepare this media. Please try again or use another browser.',
    upload: 'Upload',
    refresh: 'Refresh',
    loading: 'Loading the album…',
    empty_title: 'No photos or videos yet',
    empty_description: 'Photos and videos shared with this senior will appear here.',
    load_error: 'Could not load the album.',
    refresh_error: 'Could not refresh the album.',
    load_more: 'Load more',
    loading_more: 'Loading more…',
    retry: 'Try again',
    senior_unavailable: 'The selected senior is unavailable. Select the senior again to continue.',
    photo: 'Photo',
    video: 'Video',
    processing: 'Processing video…',
    processing_failed: 'This media item could not be processed.',
    uploaded_by: 'Uploaded by',
    you: 'You',
    created_at: 'Added on',
    description: 'Description',
    edit: 'Edit',
    delete: 'Delete',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    upload_photo: 'Upload photo or video',
    upload_progress: 'Uploading…',
    invalid_file_type: 'Choose a JPG, PNG, GIF, MP4 or MOV file.',
    upload_request_error: 'Could not start the upload.',
    upload_transfer_error: 'Could not upload the file.',
    upload_record_error:
      'The request to save the file failed, but it may still have been saved. Refresh and check the album before trying again to avoid duplicates.',
    upload_success: 'Upload successful. Videos may take time to process.',
    edit_success: 'Description updated successfully.',
    edit_error: 'Could not update the description.',
    router_prompt_edit_title: 'Leave without saving changes to this album item?',
    router_prompt_edit_message: 'By leaving, you will lose the caption changes you made.',
    delete_success: 'Album item deleted successfully.',
    delete_error: 'Could not delete this album item.',
    delete_confirm_title: 'Delete album item',
    delete_confirm_description:
      'Are you sure you want to delete this item? This action cannot be undone.',
    detail_not_found: 'This album item could not be found.',
    detail_load_error: 'Could not load this item.',
    detail_unavailable: 'This item is unavailable.',
    signed_url_error: 'Could not open this item. Try again.',
  },
  relationship_types: {
    [RelationshipType.Family]: 'Family',
    [RelationshipType.Friend]: 'Friend',
    [RelationshipType.Healthcare]: 'Healthcare',
    [RelationshipType.Service]: 'Service',
    [RelationshipType.Other]: 'Other',
  },
  user_types: {
    [UserType.Caregiver]: 'Caregiver',
    [UserType.Follower]: 'Follower',
    [UserType.Senior]: 'Senior',
  },
  genders: {
    [UserGender.Female]: 'Female',
    [UserGender.Male]: 'Male',
    [UserGender.Undisclosed]: 'Undisclosed',
    [UserGender.Unspecified]: 'Other',
  },
  marital_statuses: {
    [MaritalStatus.CivilUnion]: 'Civil union',
    [MaritalStatus.Divorced]: 'Divorced',
    [MaritalStatus.Married]: 'Married',
    [MaritalStatus.Other]: 'Other',
    [MaritalStatus.Single]: 'Single',
    [MaritalStatus.Widowed]: 'Widowed',
  },
  education_levels: {
    [EducationLevel.BachelorsDegree]: "Bachelor's degree",
    [EducationLevel.BasicEducation]: 'Basic education',
    [EducationLevel.DoctoratePhd]: 'Doctorate (PhD)',
    [EducationLevel.MastersDegree]: "Master's degree",
    [EducationLevel.NoFormalEducation]: 'No formal education',
    [EducationLevel.Other]: 'Other',
    [EducationLevel.PostgraduateDiploma]: 'postgraduate diploma',
    [EducationLevel.PrimaryEducation]: 'Primary education',
    [EducationLevel.SecondaryEducation]: 'Secondary education',
    [EducationLevel.VocationalTechnicalTraining]: 'Vocational/Technical training',
  },
  languages: {
    [BackendLanguageTag.ptPT]: 'Portuguese (Portugal)',
    [BackendLanguageTag.enUS]: 'English (US)',
    [BackendLanguageTag.esES]: 'Spanish (Spain)',
    [BackendLanguageTag.glES]: 'Galician (Spain)',
    [BackendLanguageTag.ptBR]: 'Portuguese (Brazil)',
  },
  regions: {
    [RegionCode.PT]: 'Portugal',
    [RegionCode.ES]: 'Spain',
    [RegionCode.GL]: 'Galicia',
    [RegionCode.BR]: 'Brazil',
  },
  roles: {
    [CaregiverRelationshipRole.Administrator]: 'Administrator',
    [CaregiverRelationshipRole.Contact]: 'Contact',
    [CaregiverRelationshipRole.Member]: 'Member',
  },
  religions: {
    [Religion.AfricanTraditionalAndDiasporic]: 'African traditional and diasporic',
    [Religion.Agnostic]: 'Agnostic',
    [Religion.Animism]: 'Animism',
    [Religion.Atheist]: 'Atheist',
    [Religion.BahaI]: 'Baháʼí',
    [Religion.Buddhism]: 'Buddhism',
    [Religion.CaoDai]: 'Caodaism',
    [Religion.ChineseTraditionalReligion]: 'Chinese traditional',
    [Religion.Christianity]: 'Christianity',
    [Religion.Hinduism]: 'Hinduism',
    [Religion.Islam]: 'Islam',
    [Religion.Jainism]: 'Jainism',
    [Religion.Juche]: 'Juche',
    [Religion.Judaism]: 'Judaism',
    [Religion.NeoPaganism]: 'Neopaganism',
    [Religion.Nonreligious]: 'Non religious',
    [Religion.Rastafari]: 'Rastafari',
    [Religion.Secular]: 'Secular',
    [Religion.Shinto]: 'Shinto',
    [Religion.Sikhism]: 'Sikhism',
    [Religion.Spiritism]: 'Spiritism',
    [Religion.Tenrikyo]: 'Tenrikyo',
    [Religion.UnitarianUniversalism]: 'Unitarian universalism',
    [Religion.Zoroastrianism]: 'Zoroastrianism',
    [Religion.PrimalIndigenous]: 'Indigenous',
    [Religion.Undisclosed]: 'Undisclosed',
    [Religion.Unspecified]: 'Other',
  },
  role_permissions_section: {
    overview: 'Overview',
    seniors: 'Seniors',
    team: 'Team',
    'digital-inclusion': 'Digital Inclusion',
    settings: 'Settings',
    'daily-care-log': 'Daily care log',
    calendar: 'Calendar',
    occurrences: 'Occurrences',
    'quick-access-devices': 'Quick Access',
  },
  role_permissions: {
    [RolePermissionType.Overview_Activity]: 'Activity Overview',
    [RolePermissionType.Overview_Statistics]: 'General statistics',
    [RolePermissionType.Seniors_List]: 'Seniors List',
    [RolePermissionType.Seniors_Dashboard]: 'Dashboard',
    [RolePermissionType.Seniors_Dashboard_Activity]: 'Dashboard > Activity',
    [RolePermissionType.Seniors_Dashboard_GeneralStatistics]: 'Dashboard > General Statistics',
    [RolePermissionType.Seniors_Dashboard_HealthStatistics]: 'Dashboard > Health Statistics',
    [RolePermissionType.Seniors_IndividualPlan]: 'Individual Plan',
    [RolePermissionType.Seniors_PersonalData_Reminders]: 'Reminders',
    [RolePermissionType.Seniors_BADL]: 'Care plan > BADL',
    [RolePermissionType.Seniors_IADL]: 'Care plan > IADL',
    [RolePermissionType.Seniors_ClinicalSupport]: 'Care plan > Clinical Support',
    [RolePermissionType.Seniors_PersonalDevelopment]: 'Care plan > Pers. develop.',
    [RolePermissionType.Seniors_CareAndActivities]: 'Care plan > Care summary',
    [RolePermissionType.Seniors_CareAndActivities_BADL]: 'Care plan > Care summary > BADL',
    [RolePermissionType.Seniors_CareAndActivities_IADL]: 'Care plan > Care summary > IADL',
    [RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment]:
      'Care plan > Care summary > Pers. develop.',
    [RolePermissionType.Seniors_CareAndActivities_ClinicalSupport]:
      'Care plan > Care summary > Clinical Support',
    [RolePermissionType.Seniors_CareServices]: 'Care Service',
    [RolePermissionType.Seniors_PersonalData_Profile]: 'Personal Data > Profile',
    [RolePermissionType.Seniors_PersonalData_Caregivers]: 'Personal Data > Caregivers',
    [RolePermissionType.Seniors_PersonalData_Health]: 'Personal Data > Health',
    [RolePermissionType.Seniors_DigitalInclusion_Applications]: 'Digital Inclusion > Applications',
    [RolePermissionType.Seniors_DigitalInclusion_Devices]: 'Digital Inclusion > Devices',
    [RolePermissionType.Seniors_DigitalInclusion_Playlists]: 'Digital Inclusion > Playlists',
    [RolePermissionType.Seniors_DigitalInclusion_Cards]: 'Digital Inclusion > Cards',
    [RolePermissionType.Seniors_PersonalData_Attachments]: 'Personal Data > Attachments',
    [RolePermissionType.Team_Professionals]: 'Professionals List',
    [RolePermissionType.Team_CareServices]: 'General Service Schedule',
    [RolePermissionType.Team_Professionals_Profile]: 'Professional > Profile',
    [RolePermissionType.Team_Professionals_Applications]: 'Professional > Applications',
    [RolePermissionType.Team_Professionals_Cards]: 'Professional > Cards',
    [RolePermissionType.Team_Professionals_Playlists]: 'Professional > Playlists',
    [RolePermissionType.Team_Professionals_CareServices]: 'Professional > Care Service',
    [RolePermissionType.Team_Shifts_Management]: 'Shift Management',
    [RolePermissionType.Team_Shifts_Attributions]: 'Shift Allocations',
    [RolePermissionType.Team_Permissions]: 'Permissions',
    [RolePermissionType.Team_AttendanceTracker_Registry]: 'Attendance Tracker > Registry',
    [RolePermissionType.Team_AttendanceTracker_Assignments]: 'Attendance Tracker > Assignments',
    [RolePermissionType.DigitalInclusion_Applications]: 'Applications',
    [RolePermissionType.DigitalInclusion_Media]: 'Media',
    [RolePermissionType.DigitalInclusion_Devices]: 'Devices',
    [RolePermissionType.DigitalInclusion_Cards]: 'Cards',
    [RolePermissionType.DigitalInclusion_Playlists]: 'Playlists',
    [RolePermissionType.DigitalInclusion_Services]: 'Services',
    [RolePermissionType.Settings_Entity_Profile]: 'Institution > Profile',
    [RolePermissionType.Settings_Entity_Settings]: 'Institution > Settings',
    [RolePermissionType.Settings_Licenses]: 'Licenses',
    [RolePermissionType.Settings_BADL]: 'BADL',
    [RolePermissionType.Settings_IADL]: 'IADL',
    [RolePermissionType.Settings_ClinicalSupport]: 'Clinical Support',
    [RolePermissionType.Settings_PersonalDevelopment]: 'Pers. development',
    [RolePermissionType.Settings_Templates]: 'Templates',
    [RolePermissionType.Settings_Occurrences]: 'Occurrences',
    [RolePermissionType.DailyCareLog]: 'Daily care log',
    [RolePermissionType.DailyCareLog_BADL]: 'Daily care log > BADL',
    [RolePermissionType.DailyCareLog_IADL]: 'Daily care log > IADL',
    [RolePermissionType.DailyCareLog_PersonalDevelopment]: 'Daily care log > Pers. Development',
    [RolePermissionType.DailyCareLog_ClinicalSupport]: 'Daily care log > Clinical Support',
    [RolePermissionType.DailyCareLog_Occurrences]: 'Daily care log > Occurrences',
    [RolePermissionType.Calendar]: 'Calendar',
    [RolePermissionType.Calendar_BADL]: 'Calendar > BADL',
    [RolePermissionType.Calendar_IADL]: 'Calendar > IADL',
    [RolePermissionType.Calendar_PersonalDevelopment]: 'Calendar > Pers. Development',
    [RolePermissionType.Calendar_ClinicalSupport]: 'Calendar > Clinical Support',
    [RolePermissionType.Occurrences]: 'Occurrences List',
    [RolePermissionType.SettingsQuickAccessDevices]: 'Quick Access',
  },
  affiliation_types: {
    [AffiliationType.Senior]: 'Senior',
    [AffiliationType.Caregiver]: 'Caregiver',
    [AffiliationType.Administrator]: 'Administrator',
    [AffiliationType.Manager]: 'Manager',
    [AffiliationType.Operator]: 'Operator',
    [AffiliationType.MedicalProfessional]: 'Medical professional',
  },
  screen_paths: {
    professional_caregiver_seniors: 'Seniors',
    professional_caregiver_team: 'Team',
    professional_caregiver_roles_and_permissions: 'Positions and permissions',
    senior_dashboard: 'Dashboard',
    senior_individual_plan: 'Individual plan',
    senior_care_plan: 'Care plan',
    senior_care_and_activity: 'Care summary',
    senior_personal_data: 'Personal Data',
    senior_profile: 'Profile',
    senior_caregivers: 'Caregivers',
    senior_applications: 'Applications',
    senior_devices: 'Devices',
    senior_activity: 'Activity',
    senior_user_cards: 'Cards',
    senior_statistics: 'Statistics',
    senior_attachments: 'Attachments',
    senior_health: 'Health',
    senior_playlists: 'Playlists',
    institution_settings_profile: 'Profile',
    institution_settings_settings: 'Settings',
    institution_settings_activity: 'Activity',
    institution_settings_user_cards: 'Cars',
    institution_settings_devices: 'Devices',
    institution_settings_services: 'Services',
    institution_settings_playlist: 'Playlist',
    institution_settings_statistics: 'Statistics',
    institution_settings_licenses: 'Licenses',
    institution_settings_templates: 'Templates',
    institution_settings_occurrences: 'Occurrences',
    institution_settings_quick_access_devices: 'Quick Access',
  },
  institution_settings_screen: {
    subtitle: {
      write: 'You can configure the institution settings',
      read: 'You can check the institution settings',
    },
    columns: {
      setting: 'Setting',
      active: 'Active',
    },
    attendance_tracker: 'Attendance Tracker',
    load_error_toast: 'Unable to load the settings',
    update_error_toast: 'Unable to update the setting',
  },
  attendance_tracker_registry_screen: {
    subtitle: 'Time clock records of the professionals',
    filters: {
      all_statuses: 'All states',
      all_day: 'All day',
      all_professionals: 'All professionals',
    },
    empty: 'There are no time clock records on this day.',
    load_error: 'The time clock records could not be loaded.',
    retry: 'Try again',
    waiting_exit: 'Waiting for the exit to be registered',
    add: {
      title: 'Add time clock record',
      professional: 'Professional',
      professionals_hint:
        'Only the professionals with the time clock active (mandatory or optional) are listed.',
      professionals_empty:
        'There are no professionals with the time clock active (mandatory or optional).',
      professional_placeholder: 'Choose the professional',
      kind_label: 'What do you want to register?',
      kind_session: 'Whole session (entry and exit)',
      kind_entry: 'Only the entry (session in progress)',
      kind_exit: 'Missing exit',
      open_session_info:
        'This professional has an open entry since {{date}}. Register the missing exit.',
      entry_date: 'Entry date',
      entry_time: 'Entry time',
      exit_date: 'Exit date',
      exit_time: 'Exit time',
      reason: 'Reason',
      reason_placeholder: 'E.g. the professional forgot to clock out',
      notice:
        'This record will be identified as registered by {{admin}} on behalf of {{professional}} and has no location.',
      self_warning:
        'You cannot register your own time clock here, so that nobody registers on behalf of themselves. To register your entry or exit, use the {{app}}.',
      self_action: 'Open the {{app}}',
      cancel: 'Cancel',
      continue: 'Continue',
      back: 'Back',
      confirm: 'Register',
      confirm_title: 'Confirm the record',
      confirm_description: 'Once registered, the time clock record cannot be changed or deleted.',
      summary_record: 'Record',
      success: 'Time clock record added successfully.',
      checking: 'Checking the time clock of the professional...',
      error_future: 'The date and time cannot be in the future.',
      error_order:
        'The exit must be after the entry and there cannot be another record at the same date and time.',
      error_overlap: 'The chosen dates overlap another record of the professional.',
      error_type:
        'The record does not fit in the records that the professional already has. Check the dates and times.',
      error_conflict:
        'The records of the professional changed meanwhile. Check the data and try again.',
      error_not_tracking: 'This professional does not have the time clock active.',
      error_generic: 'The time clock record could not be added. Try again.',
      validation_future: 'It cannot be in the future.',
      validation_exit_before_entry: 'The exit must be after the entry.',
      validation_exit_before_open_entry: 'The exit must be after the open entry.',
      validation_reason_required: 'Enter the reason.',
    },
    void: {
      title: 'Void session',
      description:
        'When voided, the session does not count as time worked anymore and that period is free for new records. The record is kept as voided, with your name and the reason.',
      reason: 'Reason for voiding',
      reason_placeholder: 'E.g. registered by mistake',
      cancel: 'Cancel',
      confirm: 'Void session',
      success: 'Session voided successfully.',
      error_conflict:
        'The records of the professional changed meanwhile. Refresh the list and try again.',
      error_self: 'You cannot void your own records.',
      error_generic: 'The session could not be voided. Try again.',
      validation_reason_required: 'Enter the reason.',
    },
    correct: {
      title: 'Correct session',
      description:
        'The current session will be voided and replaced by the records you enter below. The voided record is kept, and the time it had is free for the new records.',
      current_session: 'Current session',
      reason: 'Reason for the correction',
      notice:
        'The new records will be identified as registered by {{admin}} on behalf of {{professional}} and have no location. The current session will be voided, with this reason.',
      confirm_title: 'Confirm the correction',
      confirm_description:
        'The current session will be voided and the new records will take its place. Once registered, they cannot be changed or deleted.',
      summary_old: 'Session to void',
      summary_new: 'New session',
      confirm: 'Correct session',
      success: 'Session corrected successfully.',
    },
    details: {
      correct_action: 'Correct',
      void_action: 'Void session',
      duration: 'Duration',
      title: 'Record details',
      close: 'Close',
    },
  },
  attendance_tracker_assignments_screen: {
    subtitle: {
      write: 'You can define who must track attendance',
      read: 'You can check who must track attendance',
    },
    columns: {
      professional: 'Professional',
      assignment: 'Attendance tracking',
      last_change: 'Last change',
    },
    assignment_types: {
      mandatory: 'Mandatory',
      optional: 'Optional',
      not_applicable: 'Not applicable',
    },
    empty: 'There are no professionals',
    last_change_summary: 'By {{name}} on {{date}}',
    last_change_summary_no_author: '{{date}}',
    load_error_toast: 'Unable to load the assignments',
    update_error_toast: 'Unable to update the assignment',
  },
  date_time_picker: {
    cancel_button: 'Cancel',
    confirm_button: 'Confirm',
    clear_button: 'Clear',
    today: 'Today',
  },
  date_range_input: {
    title: 'Choose a time range',
    ok_button: 'Confirm',
    cancel_button: 'Cancel',
    presets: {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      '7_days': '7 days',
      '15_days': '15 days',
      '30_days': '30 days',
      '180_days': '180 days',
      '365_days': '365 days',
      custom: 'Custom',
    },
    errors: {
      invalid_date_range: 'Invalid date range',
    },
  },
  generic_header: {
    actions: {
      edit: 'Edit',
      delete: 'Delete plan',
      save: 'Save template',
      import: 'Import template',
      archivePlan: 'Archive plan',
      remove: 'Remove',
    },
  },
  user_card: {
    actions: {
      edit: 'Edit',
      delete: 'Delete',
    },
  },
  role_autocomplete: {
    unselected: 'Unselected',
    no_matching: 'Found no matching {{ type }}.',
  },
  view_type_toggler: {
    tooltip_message: 'Switch to {{ type }} view',
  },
  index_grid_screen: {
    total_counter: 'Total',
    add_button: 'Add',
    search_placeholder: 'Search',
    filter_placeholder: 'Filter',
    empty_message_add_encouraging: 'Press add to register a {{ type }}.',
    empty_search_message: {
      title: 'Sorry, we could not find any results for "{{ name }}".',
      subtitle: 'Try a different search.',
    },
    empty_filters_message: {
      title: 'The selected filter options returned no results.',
      subtitle: 'Try different filters.',
    },
    toggler: {
      grid: 'grid',
      list: 'list',
    },
  },
  login: {
    welcome: 'Welcome to the Sioslife platform!',
    welcome_daily_care_log: 'Welcome to the Sioslife registration app',
    welcome_accept_invite: 'Login to your account to accept the invitation!',
    instruction: 'Use your credentials to access',
    email_input: 'Email address',
    password_input: 'Password',
    enter_button: 'Login',
    enter_button_microsoft: 'Login with Microsoft',
    enter_button_google: 'Login with Google',
    forgot_password: 'Recover password',
    quick_access: 'Login with quick access',
    validate_invite: 'Were you invited to be a caregiver?',
    use_invite_code: 'Use invitation code',
    errors: {
      form_validation_invalid_field: 'Fill in this field to log into your account',
      [SignInError.Unknown]: 'An error occurred. Please, try again later.',
      [SignInError.InvalidEmail]: 'Invalid email address',
      [SignInError.UserDisabled]: 'User disabled',
      [SignInError.UserNotFound]: 'User not found',
      [SignInError.WrongPassword]: 'Wrong password',
      [SignInError.FailedToGetMe]:
        'Could not load user information. Please, contact the Support team.',
    },
  },
  recover_password: {
    title: 'Forgot your password?',
    subtitle: 'We will help you!',
    instructions:
      'In order to be able to define a new password, please enter your email address. We will send you a message with a link that will allow you to make the change.',
    email_input: 'Email address',
    send_button: 'Send',
    password_remembered: 'After all, I remembered my password!',
    go_back_button: 'Go back to the login screen',
    errors: {
      form_validation_invalid_field: 'Fill in this field to complete the password recovery',
      send_request_error_message: 'An error occurred. It was not possible to send the request.',
    },
    success: {
      send_request_success_message:
        'The instructions for changing your password have been sent to the email address you provided.',
    },
  },
  replace_password: {
    title: 'Set new password',
    instructions:
      'Ok! Good job! We will then define a new password. Type your new password in the two fields below to finish the process.',
    new_password_input: 'New password',
    repeat_password_input: 'Repeat the new password',
    submit_button: 'Confirm',
    password_remembered: 'After all, I remembered my password!',
    go_back_button: 'Go to the login screen',
    errors: {
      form_validation_invalid_field:
        'Fill in this field to complete the definition of the new password',
      form_validation_invalid_password_minimum_characters:
        'The password must have at least 6 characters',
      form_validation_invalid_password_blank_spaces:
        "The password can't start or end with blank spaces",
      form_validation_invalid_repeat_password_mismatch: 'Please make sure the passwords match',
      change_password_error_message:
        'An error occurred. It was not possible to change your password.',
    },
    success: {
      change_password_success_message:
        'Your password was changed. You can now sign in with your new password.',
    },
  },
  validate_invite: {
    title: 'Use invitation code',
    instructions_1:
      'To be a {{brand}} caregiver you must have an invitation to access the platform.',
    instructions_2:
      'If you have already requested an invitation, please check your email message box and/or your SMS message box and look for a {{brand}} message. In that message you will find the code you should indicate below.',
    code_input: 'Invitation code',
    send_button: 'Validate invitation and continue',
    errors: {
      form_validation_invalid_field:
        'Fill in this field to complete the validation of the invitation',
      invalid_invite_error_message:
        'The invitation you tried to use is invalid. Please, make sure you have entered the correct code. If so, request a new invitation.',
    },
  },
  accept_invite: {
    relationship_invite_title: 'Accept the invitation to be the caregiver of',
    relationship_invite_instructions:
      'To be a caregiver, you must have an account to access the {{brand}} platform. If this is your first time accepting an invitation, press "{{button_create}}". If you have already accepted other invitations in the past, use your existing account and press "{{button_login}}".',
    affiliation_invite_title: 'Accept the invitation to affiliate and be a caregiver in the entity',
    affiliation_invite_instructions:
      'To affiliate and be a caregiver, you must have an account to access the {{brand}} platform. If this is your first time accepting an invitation, press "{{button_create}}". If you have already accepted other invitations in the past, use your existing account and press "{{button_login}}".',
    affiliation_type_description:
      'This invitation allows you to be {{type}} of the entity {{name}}.',
    create_account_button: 'Accept and create new account',
    login_button: 'Log into an existing account',
    errors: {
      invalid_invite_error_message:
        'The invitation you tried to use is invalid. Please, request a new invitation.',
    },
  },
  quick_access_login: {
    instruction: 'Choose user to log in',
    all_users: 'All users',
    error:
      "It's not possible to continue with Quick Access, please try again. If the problem persists, please contact your System Administrator.",
    back_to_email_and_password_login: 'Login with E-mail and Password',
    go_back_to_the_recent_users_list: 'Go back to the recent users list',
    hello_user: 'Hello, {{name}}!',
    insert_your_quick_access_code: 'Insert your Quick Access code',
    login_failed: 'Login failed. Please try again.',
    login_failed_code: 'Quick access code is incorrect.',
    try_again: 'Try again',
    regain_access: 'Regain access code',
    remove_recent: 'Do you want to remove the user from the recent users list?',
    regain_access_message:
      'Your quick access code can be reset in the Management Area of ​the Sioslife platform, in your professional profile.',
    regain_access_description:
      'If you do not have access permissions to the Management Area, please speak to an administrator at your entity.',
    back_to_previous: 'Back to previous screen',
  },
  notifications: {
    date: 'Date',
    time: 'Time',
    title: 'Notifications',
    check_read: 'Mark as read',
    check_unread: 'Mark as unread',
    read_all_notifications: 'Mark all as read',
    read_all_notifications_dialog_title: 'Mark all notifications as read?',
    read_all_notifications_dialog_content:
      'You can individually re-mark each notification as unread later if you wish.',
    read_all_notifications_disabled_tooltip_message:
      "You've already marked all notifications as read.",
    read_all_notifications_success_message: 'The notifications have all been marked as read.',
    read_all_notifications_error_message:
      'An error occurred while marking the notifications as read.',
  },
  push_notifications: {
    warnings: {
      unsubscribed_generic_warning_title: 'You cannot receive notifications in real-time',
      unsubscribed_not_supported_warning_message:
        'The browser you are using does not support the reception of notifications. Use Chrome, Firefox or Edge to receive notifications in real-time.',
      unsubscribed_error_warning_message:
        'An error has occurred so you cannot receive notifications in real-time.',
      unsubscribed_permissions_denied_warning_message:
        'The permissions to receive notifications were denied. Please, enable notifications for the browser you are using.',
    },
  },
  invite_modal: {
    caregiver_invite_title: 'Accept the invitation to be a caregiver',
    affiliation_type_description:
      'This invitation allows you to be {{type}} of the entity {{name}}.',
    relationship_type_input: 'What type of relationship do you have with {{name}}?',
    invite_recipient_not_current_user_description:
      'You are using an account with information different from the information present in this invitation. Please, review carefully if you intend to continue.',
    accept_button: 'Yes, I accept',
    decline_button: 'No, I refuse',
    relationship_already_exists_description:
      "You are already a caregiver of {{name}} for having previously accepted an invitation. You don't need to do anything else.",
    affiliation_already_exists_description:
      "You already have an affiliation with the entity {{name}} for having previously accepted an invitation. You don't need to do anything else.",
    loading_invite_description_1: 'Loading invitation details...',
    loading_invite_description_2: 'Please wait...',
    invite_done_unknown_description: 'The invitation can no longer be used.',
    invite_done_refused_description:
      'The invitation was already refused. It can no longer be used.',
    invite_done_accepted_description:
      'The invitation was already accepted. It can no longer be used.',
    invite_done_cancelled_description: 'The invitation was cancelled. It can no longer be used.',
    invite_done_expired_description: 'The invitation expired. It can no longer be used.',
    errors: {
      refuse_invite_error_message:
        'An error occurred. It was not possible to refuse the invitation.',
      accept_invite_error_message:
        'An error occurred. It was not possible to accept the invitation.',
      fetch_invite_error_message: 'An error occurred. It was not possible to open the invitation.',
      fetch_invite_invalid_error_message: 'The invitation you tried to open is invalid.',
    },
    success: {
      refuse_relationship_invite_success_message:
        'The invitation was refused. {{name}} was not added to your contacts list.',
      accept_relationship_invite_success_message:
        'The invitation was accepted. {{name}} was added to your contacts list.',
      refuse_affiliation_invite_success_message:
        'The invitation was refused. {{name}} was not added to your affiliations.',
      accept_affiliation_invite_success_message:
        'The invitation was accepted. {{name}} was added to your affiliations.',
    },
  },
  profile: {
    title: 'Profile',
    exit_button: 'Exit',
    personal_information_section_title: 'Edit profile',
    change_theme_section_title: 'My theme',
    legal_information_section_title: 'Legal information',
    relationships_section_title: 'My relationships',
    affiliations_section_title: 'My affiliations',
    logout_normal_button: 'Logout',
    logout_loading_button: 'Logging out...',
    logout_error_button: 'Error while logging out',
    theme_input: 'Theme',
    dark_option: 'Dark',
    light_option: 'Light',
    system_option: 'System theme (auto)',
  },
  create_caregiver_account: {
    register_title: 'Registration',
    essential_information_step_description:
      'Welcome to the {{brand}} platform! We will create a user account so that you can become a caregiver and thus finish the process of accepting the invitation you received.',
    essential_information_step: 'Essential information',
    personal_information_step: 'Personal information',
    optional_information_step: 'Optional information',
    note_title: 'Note',
    note_statement:
      'The data indicated with "*" are mandatory for the creation of the caregiver access account.',
    continue_button: 'Continue',
    create_account_button: 'Accept invitation and create account',
    start_using_button: 'Start using',
    go_back_to_login_button: 'Go to the login screen',
    go_back_button: 'Go back',
    success_title: 'Success!',
    success_description:
      'Congratulations! You have just become a user of the {{brand}} platform! Thank you for joining this community. You can now go to your user account.',
    error_title: 'Unable to create the user account!',
    error_description:
      'Unfortunately something failed while creating your user account. Please, contact the Support team for help.',
    relationship_type_input: 'Relationship type with {{name}}',
    profession_input: 'Professional field',
    profession_description_input: 'Profession/Occupation',
    email_address_input: 'Email address',
    password_input: 'Password',
    repeat_password_input: 'Repeat password',
    legal_documents_title: 'Legal documents',
    legal_documents_statement:
      'I confirm that I have read and accept the <0>General Conditions of Use</0> and the <1>Privacy and Data Protection Policy</1>.',
    full_name_input: 'Full name',
    display_name_input: 'How do you like to be called',
    mobile_number_input: 'Mobile phone number',
    profile_picture_input: 'Profile picture',
    gender_input: 'Gender',
    gender_placeholder_input: 'Select a gender',
    language_input: 'Language',
    language_placeholder_input: 'Select a language',
    birthday_input: 'Birthday',
    birthday_input_placeholder: 'Select a date',
    tax_identification_number_input: 'Tax identification number',
    profile_picture_info_message: 'For best resolution, the image should be JPG/JPEG.',
    display_name_info_message:
      'In this field, indicate the name by which you are called in everyday life. This is the name that other users will see on the system when interacting with you.',
    errors: {
      form_validation_invalid_field:
        'Fill in this field to complete the creation of the caregiver account',
      form_validation_invalid_email: 'The email provided is not valid',
      form_validation_invalid_password_minimum_characters:
        'The password must have at least 6 characters',
      form_validation_invalid_password_blank_spaces:
        "The password can't start or end with blank spaces",
      form_validation_invalid_repeat_password_mismatch: 'Please make sure the passwords match',
      form_validation_invalid_full_name_maximum_characters:
        'The full name must have less than {{max}} characters',
      form_validation_invalid_display_name_maximum_characters:
        'The name must have less than {{max}} characters',
      form_validation_invalid_mobile_phone_number: 'The mobile phone number provided is not valid',
      form_validation_not_mobile_phone_number:
        'The number provided does not match a mobile phone number',
      form_validation_invalid_tax_identification_number:
        'The tax identification number provided is not valid',
      form_validation_invalid_legal_documents:
        'Accept the legal documents to complete the creation of the caregiver account',
      enter_account_error_message:
        'An error occurred. It was not possible to enter into your account.',
      invalid_invite_error_message:
        'The invitation you tried to use to create an account is invalid.',
    },
  },
  office: {
    generics: {
      cancel: 'Cancel',
      save: 'Save',
      exit: 'Exit',
      back: 'Back',
      apply: 'Apply',
      save_pdf: 'Save in PDF (.pdf)',
      save_xlsx: 'Save in Excel (.xlsx)',
    },
    localized_string_input: {
      fields: {
        placeholder: 'Write a translation',
        rules: {
          required: 'Required field',
          max_length: 'It cannot be longer than {{ char }} characters.',
        },
      },
    },
    users_filters: {
      title: 'Filters',
      clear_all: 'Clear all',
      sections: {
        socialResponses: {title: 'Care services'},
        shifts: {title: 'Shifts'},
        activeStatus: {
          title: 'Status',
          options: {
            active: 'Active',
            inactive: 'Inactive',
          },
        },
        taskStatus: {
          title: 'Status',
          options: {
            planned: 'Planned',
            handled: 'Handled',
            done: 'Done',
            not_done: 'Not done',
            not_handled: 'Not handled',
          },
        },
        taskGroups: {
          title: 'Tasks',
        },
        patientOccurrences: {
          title: 'Patient occurrences',
        },
        generalOccurrences: {
          title: 'General occurrences',
        },
      },
    },
    profile_type_tabs: {
      informal_caregiver: 'Informal',
      professional_caregiver: 'Professional',
      system_administrator: 'Sys Admin',
      partner: 'Partner',
    },
    informal_caregiver: {
      tabs: {
        activity: 'Activity',
        profile: 'Profile',
        media: 'Multimedia',
        reminders: 'Reminders',
      },
      add_menu: {
        button: 'Add',
        title: 'What do you want to add?',
        create_reminder: 'Reminder',
        send_photo: 'Photo',
        send_video: 'Video',
        send_invite: 'Caregiver invitation',
      },
      relationship_picker: {
        caption: 'You are seeing the profile of:',
        dialog_title: 'Select another contact',
      },
      caregiver_app_info_message:
        'We are still working to improve this area. To take full advantage of {{brand}} features as a family member, friend or informal caregiver, please use our application for mobile devices <0>Android</0> or <1>iOS</1>.',
    },
    professional_caregiver: {
      tabs: {
        statistics: 'Statistics',
        digital_inclusion: 'Digital inclusion',
        seniors: 'Seniors',
        applications: 'Applications',
        services: 'Services',
        professional_caregivers: 'Team',
        activity: 'Activity',
        devices: 'Devices',
        user_cards: 'Cards',
        licenses: 'Licenses',
        entity_photos: 'Photos',
        playlists: 'Playlists',
        start: 'Start',
        planner: 'Calendar',
        occurrences: 'Occurrences',
        settings: 'Settings',
        sioslifeAcademy: 'Academy',
        myToDos: 'My To-dos',
      },
      profile: {
        generic_header: {
          title: 'Profile',
          subtitle: {
            write: 'You can configure the profile and other information',
            read: 'You can check all the profile information',
          },
          breadcrumbs: {
            team: 'Team',
            professionals: 'Professionals',
            profile: 'Profile',
          },
        },
      },
      affiliation_picker: {
        caption: 'You are working on entity:',
        dialog_title: 'Select another entity',
      },
      settings: {
        generic_header: {
          breadcrumbs: {
            settings: 'Settings',
            institution: 'Institution',
            licenses: 'Licenses',
            templates: 'Templates',
            occurrences: 'Occurrences',
            quick_access_devices: 'Quick Access',
          },
          subtitle: {
            institution: {
              write: "You can configure the institution's information",
              read: 'You can check all the information about the institution',
            },
            activities_sector: "You can configure the sector's activities",
            licenses: 'You can check the licenses',
            templates: {
              write: 'You can configure all templates',
              read: 'You can check all templates',
            },
            occurrences: 'Configure and manager the occurrences types',
            quick_access_devices: 'Configure and manager the quick access devices',
          },
        },
        templates: {
          empty: 'There is no templates',
          name: 'Name',
          area: 'Area',
          description: 'Description',
          update: 'Last update',
          edit: 'Edit',
          delete: 'Delete',
          revert: 'Revert',
          duplicate: 'Duplicate',
          cancel: 'Cancel',
          delete_title: 'Are you sure you want to delete the "{{name}}" template?',
          delete_subtitle: 'The care plan will be maintained for users',
          snackbar: {
            delete_success_message: 'Template "{{name}}" deleted successfully.',
            delete_error_message: 'Could not delete template "{{name}}".',
            duplicate_success_message: 'Template "{{name}}" was duplicated.',
            duplicate_error_message: 'Could not duplicate template "{{name}}".',
            edit_name_success_message: 'Template "{{name}}" was edited successfully.',
            edit_name_error_message: 'Could not edit template "{{name}}".',
          },
          edit_name: 'Edit name',
          alert: 'Changes made to this model will not affect the users.',
          no_permissions: 'You do not have permission to edit templates.',
        },
        occurrences: {
          patients: 'Users',
          general: 'General',
          general_plural: 'General',
          occurrence: 'Occurrence',
          active: 'Active',
          action_required: 'Requires action',
          notify: 'Profiles to notify',
          all_profiles: 'All profiles',
          add: 'Add',
          add_modal_title: 'Add occurrence type - ',
          add_modal_name: 'Occurrence type name',
          add_name_error: 'Name already exists',
          delete_modal_title: 'Are you sure you want to delete the occurrence type "{{name}}"?',
          revert_modal_title: 'Are you sure you want to revert the occurrence type "{{name}}"?',
          delete_modal_subtitle:
            'The occurrence type will no longer be available for registration.',
          revert_modal_subtitle: 'The occurrence type will be reverted to its default values.',
          name_error: 'The occurrence name must have less than {{max}} characters',
          add_error_toast: 'It was not possible to add the occurrences type "{{name}}".',
          add_success_toast: 'The occurrence type was added successfully!',
          edit_error_toast: 'It was not possible to edit the occurrences type "{{name}}".',
          edit_success_toast: 'The occurrence type was edited successfully!',
          delete_error_toast: 'It was not possible to delete the occurrences type "{{name}}".',
          delete_success_toast: 'The occurrence type "{{name}}" was deleted successfully!',
          revert_success_toast: 'The occurrence type "{{name}}" was reverted successfully!',
          delete_default:
            "Default configuration - It's not possible to delete this type of occurrence.",
          edit_name_default: 'Default configuration - It is not possible to edit the name.',
          default: 'Default configuration',
          empty_general: 'There are no general occurrences registered yet.',
          empty_patients: 'There are no occurrences registered for users yet.',
          no_permissions: 'You do not have permission to edit occurrences types.',
        },
        quick_access: {
          empty: 'There is no quick access devices',
          name: 'Name',
          type: 'Type',
          access: 'Last access',
          professional: 'Professional',
          deactivate: 'Deactivate',
          activate_device: 'Activate on this device',
          activate_disabled: 'Quick access is already active on this device',
          activate_disabled_ist:
            'Quick access is already active on this device on the institution "{{name}}"',
          no_permissions: 'You do not have permission to edit quick access devices.',
          activate_modal: {
            title: 'Activate quick access on this device',
            device_name: 'Device name',
            device_type: 'Device type',
            success_message: 'Quick access activated successfully on this device!',
            error_message: 'It was not possible to activate quick access on this device!',
            name_repeat_error: 'This device name is already in use',
          },
          edit_modal: {
            title: 'Edit device',
            success_message: 'The quick access device was edited successfully!',
            error_message: 'It was not possible to edit the quick access device!',
          },
          deactivate_modal: {
            title: 'Are you sure you want to deactivate the quick access device "{{name}}"?',
            subtitle: 'The device can continue to be used normally with email and password.',
            success_message: 'Quick access deactivated successfully on this device!',
            error_message: 'It was not possible to deactivate quick access on this device!',
            button: 'Deactivate',
            no_name: 'No name',
          },
        },
      },
      seniors: {
        edit: {
          profile: {
            generic_header: {
              subtitle: {
                write: 'You can configure the profile and other important information',
              },
            },
          },
        },
        table_view_screen: {
          name: 'Name',
          social_response: 'Care services',
          age: 'Age',
          room: 'Room',
          status: 'Status',
          role: 'Role',
          phone: 'Phone',
          email: 'Email',
        },
      },
      shifts_allocations: {
        generic_header: {
          breadcrumbs: {
            team: 'Team',
            shifts_allocations: 'Shift allocations',
          },
        },
        exit_edit_mode: 'Exit edit mode',
        table: {
          collaborators: 'Collaborators',
          today: 'Today',
          select_shift: 'Select shift',
        },
      },
      roles_and_permissions: {
        generic_header: {
          breadcrumbs: {
            team: 'Team',
            roles_and_permissions: 'Roles and Permissions',
          },
          subtitle: 'Create roles and manage permissions',
        },
        create: 'Create',
        edit: 'Edit',
        delete: 'Delete',
        default_role: 'Default Configuration',
        confirm_delete: {
          title: 'Delete role - {{ name }}',
          copy: {
            title: 'Are you sure you want to delete this role?',
            subtitle: 'This action cannot be undone.',
          },
          snackbar: {
            error_message: 'An error occurred while deleting the role.',
          },
        },
      },
      occurrences: {
        empty_title: 'There are no occurrences registered yet.',
        empty_subtitle: 'Press add to register an occurrence.',
        table_header_date: 'Date',
        table_header_time: 'Time',
        table_header_type: 'Type',
        table_header_patient: 'Patient',
        table_header_responsible: 'Responsible',
        table_header_registered: 'Registered by',
        table_header_status: 'Status',
        table_header_knowledge: 'Knowledge',
        add_occurrence: 'Add occurrence',
        type_placeholder: 'Select an occurrence type',
        local_placeholder: 'Write the occurrence location',
        patient_placeholder: 'Select a patient',
        responsible_placeholder: 'Select a responsible',
        details_placeholder: 'Write the occurrence details',
        status: {
          registered: 'Registered',
          not_handled: 'To be handled',
          handled: 'Handled',
        },
        details: 'Details',
        information: 'Occurrence information',
        date_hour: 'Date and time',
        acknowledge: 'Took knowledge',
        add_notes: 'Add notes',
        write_notes: 'Write a note',
        acknowledge_success: 'The occurrence was acknowledged.',
        acknowledge_error: 'It was not possible to acknowledge the occurrence.',
        handle_success: 'The occurrence was handled successfully.',
        handle_error: 'It was not possible to handle the occurrence.',
        responsible: 'Responsible',
        registered_by: 'Registered by',
        took_acknowledge: 'Took knowledge',
        history: {
          acknowledge: 'Took knowledge of the occurrence',
          state_update: 'Updated the occurrence status',
        },
        notes_success: 'The notes were added successfully.',
        notes_error: 'It was not possible to add the notes.',
      },
    },
    system_administrator: {
      tabs: {
        statistics: 'Statistics',
        activity: 'Activity',
        devices: 'Devices',
        apiDocs: 'API Docs',
        applications: 'Applications',
        entities: 'Entities',
        cards: 'Cards',
        licenses: 'Licenses',
        seniors: 'Seniors',
        caregivers: 'Caregivers',
        playlists: 'Playlists',
        gdprErasureRequests: 'Right to Erasure (GDPR)',
      },
    },
    partner: {
      tabs: {
        home: 'Home',
        apiDocs: 'API Docs',
      },
    },
    legacy_integration: {
      entity_services_disabled_alert_title: 'Unable to perform the action.',
      entity_services_disabled_alert_message:
        "Sorry, you don't have access to this area. Please, contact the Support team for more information.",
    },
    none: {
      tabs: {
        home: 'Home',
      },
    },
    permissions: {
      access_denied_message: "Sorry, you don't have permission to access this page.",
    },
    access: {
      denied_message_first: 'To have access to this area is necessary to have the Sioslife ',
      denied_message_second: ' module.',
      care_management: 'Care Management',
      digital_inclusion: 'Digital Inclusion',
      speak: 'Speak to us!',
      tooltip:
        'To have access to this option is necessary to have the Sioslife <strong>{{ module }}</strong> module.',
    },
    pending_tasks: {
      alert:
        'Attention!<br>We identified that there are {{number}} tasks already realized, pending validation.',
      see_tasks: 'See tasks',
      header_title: 'Pending tasks',
      info: 'The tasks presented are considered to be carried out automatically, whenever users use the Sioslife interactive system.<br>Registration will only be considered effective after the respective tasks have been validated.',
      empty: 'There are no pending tasks.',
      all: 'Select all',
      validate: 'Validate tasks',
      modal_title: 'Are you sure that you want to validate {{number}} tasks?',
      modal_observation: 'Write an comment',
      modal_validate: 'Validate',
      snackbar_success: 'All tasks were validated successfully.',
      snackbar_error: 'It was not possible to validate the tasks.',
      modal_task_validation_title:
        'Attention!<br>We identified that this task was already realized and is pending validation.',
    },
  },
  seniors: {
    root: {
      title: 'Seniors',
      add_button: 'Add',
      search_input_placeholder: 'Search seniors',
      name_header: 'Name',
      gender_header: 'Gender',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      no_seniors: 'No seniors registered yet.',
      deleted_senior_dialog_title: 'Senior "{{name}}\'s" profile deleted',
      deleted_senior_dialog_message:
        "The senior's profile no longer exists in the list of seniors.",
      deleted_senior_dialog_ok_button: 'Ok',
      senior: 'Senior',
      seniors: 'Seniors',
      modals: {
        add_senior_flow: {
          form: {
            title: 'Create Senior',
            cancel: 'Cancel',
            add: 'Add',
            inputs: {
              active: 'Active',
              name: {
                label: 'Full Name',
                placeholder: 'First and last name',
                required_message: 'Write a first and last name',
                pattern_message: 'The name you entered is not valid',
                max_message: 'The entered name cannot exceed {{ characters }} characters',
              },
              name_alias: {
                label: 'How do you like to be called',
                placeholder: 'Name',
                required_message: 'Write a name',
                pattern_message: 'The name you entered is not valid',
                max_message: 'The entered name cannot exceed {{ characters }} characters',
              },
              birthdate: {
                label: 'Birthdate',
                placeholder: 'Select a date of birth',
                required_message: 'Select a date of birth',
              },
            },
          },
          confirm: {
            title: 'Create Senior',
            confirm_title: 'Are you sure you want to add "{{ name }}"?',
            confirm_subtitle: 'The new senior will be added to the system.',
            cancel: 'Cancel',
            add: 'Add',
          },
          snackbar: {
            success_message: 'The senior "{{name}}" has been successfully created!',
            error_message: 'Error saving',
          },
        },
      },
    },
    edit: {
      senior_dcm: {
        careplan: {
          load_error: 'Could not load care plan data.',
          caregiver: {
            title: 'Responsible',
            unnasigned: 'Not assigned',
            unnasigned_tooltip: "You don't have permissions to edit the Responsible",
          },
          activities: {
            add_button: 'Add',
            load_error: 'Could not load activities groups to add.',
            empty_state: {
              title: 'There are no activities registered yet.',
              subtitle: 'Press add to register an activity.',
            },
          },
          on_save: {
            snackbar: {
              success_message: 'Care plan saved',
              error_message: 'Could not save care plan.',
            },
          },
          on_update: {
            snackbar: {
              success_message: 'Care plan updated',
              error_message: 'Could not update care plan.',
            },
          },
          on_delete: {
            title: "Are you sure you want to delete {{name}}'s care plan?",
            description: 'This action will delete the care plan permanently.',
            snackbar: {
              success_message: 'Care plan deleted',
              error_message: 'Could not delete care plan.',
            },
          },
        },
        individual_plan: {
          add_cares: 'Add care',
          caregiver: {
            title: 'Responsible',
            coordinator: 'Coordinator',
            unassigned: 'Not defined',
            unassigned_tooltip: 'You do not have permissions to edit the Responsible',
          },
          observations: 'Observations',
          created_at: 'Created at',
          valid_until: 'Valid until',
          not_defined: 'Not defined',
          empty_state: {
            title: 'There are no diagnoses registered in the individual plan yet.',
            subtitle: 'Press to add a diagnosis.',
          },
          new_diagnostic: 'New diagnosis',
          diagnostic: 'Diagnosis',
          goal: 'General goal',
          new_goal: 'New general goal',
          new_objective: 'New specific goal',
          history: {
            action: 'History',
            title: 'Individual plan history',
            close: 'Close',
            version: 'Version {{revision}}',
            unknown_author: 'Unknown user',
            load_error: 'Could not load the history.',
            source: {
              manual: 'Manual',
              ai: 'AI',
              restore: 'Restore',
            },
            summary: {
              entity: {
                plan: 'Plan',
                diagnostic: 'Diagnosis',
                goal: 'General goal',
                objective: 'Specific goal',
              },
              entity_plural: {
                plan: 'plans',
                diagnostic: 'diagnoses',
                goal: 'general goals',
                objective: 'specific goals',
              },
              field: {
                definition: 'definition',
                responsibleUserId: 'person in charge',
                cares: 'cares',
                activityTypesIds: 'associated activities',
                indicators: 'indicators',
                isActive: 'status',
                coordinatorUserId: 'coordinator',
                validUntil: 'validity',
                observations: 'observations',
                diagnosticsOrder: 'diagnoses order',
              },
              created: '{{entity}} created: "{{name}}"',
              created_unnamed: '{{entity}} created',
              created_many: '{{count}} {{entities}} created',
              deleted: '{{entity}} deleted: "{{name}}"',
              deleted_unnamed: '{{entity}} deleted',
              deleted_many: '{{count}} {{entities}} deleted',
              updated: 'Change in {{entity}}: {{field}}',
              updated_many: 'Change in {{count}} {{entities}}: {{field}}',
              more: 'and {{count}} more changes',
              restored_from: 'Restored from version {{revision}}',
            },
            restore: {
              button: 'Restore this version',
              prompt_title: 'Restore version {{revision}}?',
              prompt_description:
                'The individual plan will have the content of this version. A new version is created and no previous version is deleted, so you can go back at any time.',
              confirm: 'Restore',
              success: 'Version {{revision}} restored.',
              conflict: 'The plan was changed in the meantime. It was refreshed, please try again.',
              error: 'Could not restore the version.',
            },
            view_version: 'View version',
            back: 'Back',
            detail_load_error: 'Could not load this version.',
            no_diagnostics: 'This version has no diagnoses.',
            detail_note: 'State of the plan in this version (read only).',
            change_type_unknown: 'Change',
            change_type: {
              baseline: 'Initial state (before the history)',
              'plan-created': 'Plan created',
              'plan-updated': 'Plan updated',
              'plan-archived': 'Plan archived',
              'plan-reactivated': 'Plan reactivated',
              'diagnostic-created': 'Diagnosis created',
              'diagnostic-updated': 'Diagnosis updated',
              'diagnostic-deleted': 'Diagnosis deleted',
              'goal-created': 'General goal created',
              'goal-updated': 'General goal updated',
              'goal-deleted': 'General goal deleted',
              'objective-created': 'Specific goal created',
              'objective-updated': 'Specific goal updated',
              'objective-deleted': 'Specific goal deleted',
              'bulk-append': 'Diagnoses added in bulk',
              'bulk-replace': 'Diagnoses replaced in bulk',
              restored: 'Version restored',
            },
          },
          ai_generate: {
            title: 'Generate plan with AI',
            description:
              'Enter the clinical topic you want to prioritize or let the AI decide with the automatic plan. The text only indicates the topic, it is not an instruction to the AI.',
            focus_label: 'Plan focus',
            focus_placeholder: 'E.g. plan focused on cognitive activities to reduce mental decline',
            automatic: 'Automatic plan',
            with_focus: 'Generate with focus',
          },
          ai_proposal: {
            title: 'AI proposal',
            subtitle:
              'This is a preview. Nothing is saved to the individual plan until you approve.',
            discard: 'Discard',
            approve: 'Approve',
            approved: 'AI proposal added to the individual plan.',
            replaced:
              'Individual plan replaced by the AI proposal. The previous version was kept in the history.',
            apply_prompt: {
              title: 'Do you want to delete the data of the existing individual plan?',
              description:
                'The individual plan already has diagnoses. You can delete them and keep only the AI proposal, or add the proposal to the existing diagnoses. If you delete, the previous version is kept in the plan history.',
              replace: 'Delete plan',
              append: 'Add to plan',
            },
            rate_limited:
              'You have reached the hourly limit of AI generations. Try again in {{minutes}} min.',
            save_error: 'Could not save the proposal to the individual plan.',
            invalid_references:
              'Could not save: the proposal exceeds the allowed limits or has invalid references.',
            error: 'Could not generate the plan with AI. Please try again.',
          },
          on_delete: {
            title: 'Are you sure you want to archive the individual plan of user "{{name}}"?',
            description: 'This action will archive the individual plan. You can consult it later',
            snackbar: {
              success_message: 'Individual plan archived',
              error_message: 'Individual plan could not be archived',
            },
          },
          add_diagnostic: {
            title: 'Add diagnosis',
            edit: 'Edit diagnosis',
            form: {
              definition: {
                label: 'Definition',
                placeholder: 'Short description of the diagnosis',
                error_message: 'The definition must be between 1 and {{max}} characters.',
              },
            },
          },
          on_diagnostic_remove: {
            header: 'Delete diagnosis',
            title: 'Are you sure you want to delete the diagnosis "{{name}}"?',
            description: 'This action will delete the diagnosis permanently.',
          },
          add_diagnostic_goal: {
            title: 'Add general objective',
            edit: 'Edit general objective',
            form: {
              definition: {
                label: 'Definition',
                placeholder: 'Short description of the general objective',
                error_message: 'The definition must be between 1 and {{max}} characters.',
              },
              responsibleUser: {
                label: 'Responsible',
                placeholder: 'Select a responsible',
              },
            },
          },
          on_diagnostic_goal_remove: {
            header: 'Delete general objective',
            title: 'Are you sure you want to delete the general objective "{{name}}"?',
            description: 'This action will delete the general objective permanently.',
          },
          add_diagnostic_goal_objective: {
            title: 'Add specific objective',
            edit: 'Edit specific objective',
            form: {
              definition: {
                label: 'Definition',
                placeholder: 'Short description of the specific objective',
                error_message: 'The definition must be between 1 and {{max}} characters.',
              },
            },
            cares: {
              add_non_automatic: 'Add non-automatic care',
              non_automatic_title: 'Non-automatic cares',
              max_reached: 'You can add at most {{max}} non-automatic cares.',
              too_long: 'The non-automatic care cannot have more than {{max}} characters.',
            },
            snackbar: {
              error: 'Could not save the specific objective.',
              invalid_references:
                'Could not save: one of the selected cares or indicators is not available for this entity.',
            },
            table: {
              definition: 'Specific objective',
              cares: 'Cares',
              indicators: 'Indicators',
              targets: 'Targets',
              execution: 'Execution',
            },
          },
          on_diagnostic_goal_objective_remove: {
            header: 'Delete specific objective',
            title: 'Are you sure you want to delete the specific objective "{{name}}"?',
            description: 'This action will delete the specific objective permanently.',
          },
        },
      },
    },
  },
  senior_tabs: {
    tabs: {
      profile: 'Profile',
      caregivers: 'Caregivers',
      applications: 'Applications',
      devices: 'Devices',
      activity: 'Activity',
      statistics: 'Statistics',
      user_cards: 'Cards',
      health: 'Health',
      affiliations: 'Affiliations',
      playlists: 'Playlists',
      external_accounts: 'External Accounts',
    },
    errors: {
      senior_does_not_exist_error: 'It was not possible to get the senior you tried to access.',
    },
  },
  health_profile_form: {
    clinical_conditions_title: 'Pathologies/Limitations',
    visual_difficulty_label: 'Visual difficulties',
    hearing_difficulty_label: 'Hearing difficulties',
    cognitive_deficit_label: 'Cognitive deficit',
    parkinson_label: 'Parkinson',
    alzheimer_label: "Alzheimer's",
    other_dementias_label: 'Other dementias',
    reduced_mobility_upper_limbs_label: 'Reduced mobility of the upper limbs',
    reduced_mobility_lower_limbs_label: 'Reduced mobility of the lower limbs',
    paralysis_left_limb_label: 'Left upper limb paralysis',
    paralysis_right_limb_label: 'Right upper limb paralysis',
    wheelchair_label: 'Wheelchair',
    illiterate_label: 'Illiteracy',
    disable_clinical_conditions_tooltip: 'You do not have permissions to edit clinical conditions',
    errors: {
      update_health_profile_error_message:
        'An error occurred. It was not possible to update the health profile.',
    },
    noHealthMeasurements: 'There are no health measurements.',
  },
  health_charts: {
    noData: 'There is no data to display.',
    heartRate: {
      subtitle1: 'normal up to 120bpm',
    },
    bloodOxygen: {
      subtitle1: 'minimum 50%',
      subtitle2: 'maximum 100%',
    },
    bloodPressure: {
      subtitle1: 'Systolic: up to 120mmHg',
      subtitle2: 'Diastolic: up to 80mmHg',
    },
    bloodGlucose: {
      subtitle1: 'minimum 80mg/dL',
      subtitle2: 'maximum 180mg/dL',
    },
    bodyTemperature: {
      subtitle1: 'normal up to 36ºC',
    },
    bodyWeight: {
      subtitle1: undefined,
    },
    errors: {
      fetch: 'An error occurred while loading health data.',
    },
  },
  health_measurements: {
    title: 'Latest Measurements',
    units: {
      beatsPerMinute: 'bpm',
      breathsPerMinute: 'bpm',
    },
  },
  senior_caregivers_listing: {
    invite_caregiver_button: 'Invite caregiver',
    invites_history_button: 'See invitations',
    no_caregivers: 'There are no caregivers.',
    name_header: 'Name',
    relationship_type_header: 'Type',
    remove_relationship_header: 'Remove relationship',
    remove_relationship_dialog_title:
      'Are you sure you want to remove the relationship between the senior {{senior}} and the caregiver {{caregiver}}?',
    remove_relationship_dialog_message:
      'By removing this relationship, the senior {{senior}} will no longer be able to communicate with the caregiver {{caregiver}}.',
    remove_relationship_button: 'Remove',
    remove_relationship_button_tooltip: 'You do not have permissions to remove the relationship',
    remove_relationship_error_message:
      'It was not possible to remove the relationship between the senior {{senior}} and the caregiver {{caregiver}}.',
    remove_relationship_success_message:
      'The relationship between the senior {{senior}} and the caregiver {{caregiver}} was removed successfully.',
  },
  senior_relationships_invites_history: {
    title: 'Invitations',
    no_invites: 'There are no invitations.',
    invite_caregiver_button: 'Invite caregiver',
    cancel_button: 'Cancel',
    process_closed: 'Closed',
    name_header: 'Name',
    contact_header: 'Contact',
    state_header: 'Invitation state',
    status_header: 'Process',
    cancel_invite_dialog_title:
      "Are you sure you want to cancel the invitation for {{caregiver}} to be {{senior}} caregiver's?",
    cancel_invite_dialog_message:
      'By cancelling this invitation, the caregiver {{caregiver}} will not be able to accept the invitation to communicate with the senior {{senior}}.',
    cancel_invite_dialog_action_yes_button: 'Cancel invitation',
    cancel_invite_dialog_action_no_button: 'Keep invitation',
    cancel_invite_error_message:
      "It was not possible to cancel the invitation for {{caregiver}} to be {{senior}} caregiver's.",
    cancel_invite_success_message:
      'The invitation for {{caregiver}} to be {{senior}} caregiver was cancelled.',
  },
  informal_caregiver_invite_form: {
    title: 'Invite caregiver',
    name_input: 'Name',
    contact_method_input: 'Contact method',
    email_input: 'Email address',
    mobile_phone_number_input: 'Mobile phone number',
    save_button: 'Send invitation',
    saving_button: 'Sending...',
    save_dialog_title: 'Send invitation to {{name}}?',
    save_dialog_message:
      'By sending, {{name}} will receive an invitation to communicate and have access to some information of {{senior}}.',
    save_dialog_action_save_button: 'Send',
    router_prompt_title: 'Are you sure you want exit without sending the invitation?',
    router_prompt_message: 'By exiting, you will lose all information entered in the invitation.',
    cancel_toast: 'The invitation was cancelled.',
    success_message: 'The invitation was sent to {{name}}.',
    errors: {
      form_validation_invalid_field: 'Fill in this field to complete the invitation',
      form_validation_invalid_mobile_phone_number: 'The mobile phone number provided is not valid',
      form_validation_not_mobile_phone_number:
        'The number provided does not match a mobile phone number',
      form_validation_invalid_email: 'The email address provided is not valid',
      error_message: 'It was not possible to send the invitation to {{name}}.',
    },
  },
  devices_associations: {
    identifier_header: 'Identifier',
    type_header: 'Type',
    state_header: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    remove_device_header: 'Remove device',
    no_devices: 'There are no devices.',
    associate_device_button: 'Associate device',
    remove_device_dialog_title:
      'Are you sure you want to remove the association between the device with identifier "{{identifier}}" and {{name}}?',
    remove_device_dialog_message:
      'By removing the association, {{name}} will no longer be able to use the device to enjoy the {{brand}} system.',
    remove_device_button: 'Remove',
    remove_device_button_disabled_tooltip: 'You do not have permissions to remove the device',
    remove_device_error_message:
      'It was not possible to remove the association with the device "{{identifier}}".',
    remove_device_success_message:
      'The association with the device "{{identifier}}" was removed successfully.',
  },
  user_cards_associations: {
    name_header: 'Name',
    state_header: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    remove_card_header: 'Remove card',
    associate_card_button: 'Associate card',
    no_cards: 'There are no cards.',
    remove_card_dialog_title:
      'Are you sure you want to remove the association between the card "{{card}}" and {{name}}?',
    remove_card_dialog_message:
      'By removing the association, {{name}} will no longer be able to use the card to enjoy the {{brand}} system.',
    remove_card_button: 'Remove',
    remove_card_button_disabled_tooltip: 'You do not have permissions to remove the card',
    remove_card_error_message:
      'It was not possible to remove the association with the card "{{identifier}}".',
    remove_card_success_message:
      'The association with the card "{{identifier}}" was removed successfully.',
  },
  associate_user_card_form: {
    title: 'Associate card',
    card_name_input: 'Card name',
    entity_input: 'Entity',
    card_name_info_message: 'This field can only be filled in if an entity is previously selected.',
    save_button: 'Save',
    saving_button: 'Saving...',
    save_dialog_title: 'Associate the card "{{card}}" with {{name}}?',
    save_dialog_message:
      'By associating the card, {{name}} will be able to use it to enjoy the {{brand}} system.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Associate',
    router_prompt_title: 'Are you sure you want exit without associating the card?',
    router_prompt_message: 'By exiting, you will lose all information entered.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    cancel_toast: 'The association was cancelled.',
    no_affiliations_info_message:
      'The user has no affiliation with any entity. Therefore, it is not possible to associate cards with the user.',
    success: {
      save_success_button: 'Saved!',
      success_message: 'The card was associated with {{name}}.',
    },
    errors: {
      error_message: 'It was not possible to associate the card with {{name}}.',
      error_on_save_button: 'Error while saving',
      form_validation_invalid_field: 'Fill in this field to complete the card association',
    },
  },
  associate_device_form: {
    title: 'Associate device',
    identifier_input: 'Device identifier',
    entity_input: 'Entity',
    save_button: 'Save',
    saving_button: 'Saving...',
    save_dialog_title: 'Associate the device with identifier "{{device}}" with {{name}}?',
    save_dialog_message:
      'By associating the device, {{name}} will be able to use it to enjoy the {{brand}} system.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Associate',
    router_prompt_title: 'Are you sure you want exit without associating the device?',
    router_prompt_message: 'By exiting, you will lose all information entered.',
    cancel_toast: 'The association was cancelled.',
    no_affiliations_info_message:
      'The user has no affiliation with any entity. Therefore, it is not possible to associate devices with the user.',
    success: {
      save_success_button: 'Saved!',
      success_message: 'The device was associated with {{name}}.',
    },
    errors: {
      error_message: 'It was not possible to associate the device with {{name}}.',
      error_on_save_button: 'Error while saving',
      form_validation_invalid_field: 'Fill in this field to complete the device association',
    },
  },
  external_accounts: {
    types: {
      call: 'Voximplant',
      partner_p3_n1: 'Hope Care',
      partner_p3_n2: 'Altice SmartAL',
      partner_p3_n3: 'DeepClick',
      partner_p3_n4: 'Itelcare (Kerox)',
    },
    list: {
      type_header: 'Type',
      delete_account_header: 'Delete',
      delete_account_button: 'Delete',
      associate_account_button: 'Add account',
      no_accounts: 'There are no external accounts.',
      error_message: 'Could not get the external accounts.',
      delete_account_dialog_title: 'Delete the {{type}} account?',
      delete_account_dialog_description: 'Are you sure you want to delete this external account?',
      delete_account_dialog_cannot_be_undone: 'This action cannot be undone.',
      delete_account_dialog_confirm_button: 'Delete',
      delete_account_dialog_cancel_button: 'Cancel',
      delete_account_success_message: 'The {{type}} account was successfully deleted.',
      delete_account_error_message: 'Could not delete the {{type}} account.',
    },
    associate: {
      title: 'Add external account',
      type_input: 'Account type',
      save_button: 'Create',
      saving_button: 'Creating...',
      save_dialog_title: 'Create the {{type}} account?',
      save_dialog_message: 'Are you sure you want to create this external account?',
      save_dialog_action_cancel_button: 'Cancel',
      save_dialog_action_save_button: 'Create',
      no_available_types_message:
        "There's already an external account of every available type for this user.",
      success_message: 'The {{type}} account was successfully created.',
      errors: {
        generic_message: 'Could not create the external account.',
        conflict_message: 'An external account of this type already exists for this user.',
        provider_error_message:
          'The external provider failed to create the account. Please try again later.',
        error_on_save_button: 'Error',
      },
    },
    details: {
      delete_button: 'Delete account',
      fields: {
        password: 'Password',
        application_name: 'Application name',
        username: 'Username',
        language: 'Language',
        project_identification: 'Project identification',
        serial: 'Serial number',
        profile: 'Profile',
        timezone: 'Timezone',
        institution_id: 'Institution ID',
        instance_name: 'Instance name',
        manufacturer: 'Manufacturer',
        model: 'Model',
        user_id: 'User ID',
        patient_id: 'Patient ID',
        email: 'Email',
      },
      errors: {
        not_found_message: 'Could not get the external account.',
      },
    },
  },
  caregivers: {
    root: {
      title: 'Caregivers',
      search_input_placeholder: 'Search caregivers',
      name_header: 'Name',
      email_header: 'Email',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      no_caregivers: 'There are no caregivers.',
    },
  },
  professional_caregivers: {
    root: {
      title: 'Professionals',
      invite_button: 'Invite professional',
      search_input_placeholder: 'Search professionals',
      name_header: 'Name',
      email_header: 'Email',
      affiliation_type_header: 'Type',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      no_professional_caregivers: 'There are no professionals.',
      deleted_professional_caregiver_dialog_title: "Professional {{name}}'s profile deleted",
      deleted_professional_caregiver_dialog_message:
        "The professional's profile no longer exists in the list of professionals.",
      deleted_professional_caregiver_dialog_ok_button: 'Ok',
      colaborator: 'colaborator',
      colaborators: 'colaborators',
      modals: {
        add_staff_flow: {
          form: {
            title: 'Add Collaborator',
            cancel: 'Cancel',
            send_invite: 'Sent invite',
            create_profile: 'Create profile',
            inputs: {
              name: {
                label: 'Name',
                placeholder: 'Name and surname',
                required_message: 'Type the full name',
                pattern_message: 'The name field is not valid',
                max_message: 'The entered name cannot exceed {{ characters }} characters',
              },
              email: {
                label: 'Email',
                placeholder: 'Write an email',
                required_message: 'The email field is required',
                pattern_message: 'The email field is not valid',
              },
              roles: {
                label: 'Roles',
                placeholder: 'Select roles',
              },
              invitation_type: {
                label: 'How to create the profile?',
                placeholder: 'Select an option',
                required_message: 'You must select a sending option',
                via_email: 'Invite by e-mail address',
                via_sms: 'Invite by SMS Message',
                via_quickAccessCode: 'Create with Quick Access Code',
              },
              phone_number: {
                label: 'Mobile/phone number',
                placeholder: 'Enter a mobile/phone number',
                required_message: 'The mobile/phone number field is required',
                is_valid_message: 'Invalid mobile/phone number',
              },
              quick_access_code: {
                label: 'Quick Access Code',
                placeholder: 'Enter a 4-digit quick access code',
                required_message: 'The quick access code field is required',
                is_valid_message: 'Quick access code invalid',
              },
            },
          },
          confirm: {
            title: 'Send invitation',
            title_quickAccessCode: 'Create profile',
            confirm_title: 'Are you sure you want to send an invitation to {{name}}?',
            confirm_title_quickAccessCode:
              'Are you sure you want to create a profile for {{name}}?',
            confirm_subtitle:
              '{{name}} will now have access to some information about {{institution}}.',
            cancel: 'Cancel',
            send_invite: 'Send invitation',
            create_profile: 'Create profile',
          },
          success: {
            title: 'Send invitation',
            title_quickAccessCode: 'Create profile',
            confirm_title: 'The invitation has been sent!',
            confirm_title_quickAccessCode: 'The profile has been created!',
            confirm_subtitle:
              'The contributor will be added fully to the team after confirming the invitation.',
            confirm_subtitle_quickAccessCode:
              'The employee has been fully added to the team and should change his quick access code as soon as possible.',
            confirm: 'Fechar',
          },
          snackbar: {
            success_message: 'The invitation has been sent to {{name}}.',
            error_message: 'An error occurred while sending the invitation',
            success_message_create_profile: 'The profile has been created for {{name}}.',
            error_message_create_profile: 'An error occurred while creating the profile',
          },
        },
      },
    },
  },
  caregiver_tabs: {
    tabs: {
      profile: 'Profile',
      affiliations: 'Affiliations',
      applications: 'Applications',
      user_cards: 'Cards',
      playlists: 'Playlists',
    },
    errors: {
      caregiver_does_not_exist_error:
        'It was not possible to get the caregiver you tried to access.',
    },
  },
  user_affiliations: {
    entity_name_header: "Entity's name",
    entity_type_header: "Entity's type",
    affiliation_type_header: "Affiliation's type",
    remove_affiliation_header: 'Remove affiliation',
    remove_affiliation_button: 'Remove',
    remove_affiliation_dialog_title:
      'Remove the affiliation between the user {{user}} and the entity {{entity}}?',
    remove_affiliation_dialog_message:
      'By removing this affiliation, the user {{user}} will no longer have access to the entity {{entity}}.',
    remove_affiliation_dialog_action_yes_button: 'Yes, remove',
    remove_affiliation_dialog_action_no_button: 'Cancel',
    update_affiliation_dialog_title:
      'Change the affiliation type of the user {{user}} with the entity {{entity}}?',
    update_affiliation_dialog_message:
      'By changing, the user\'s affiliation type will be updated to "{{type}}" in the {{entity}} entity.',
    update_affiliation_dialog_action_yes_button: 'Yes, change',
    update_affiliation_dialog_action_no_button: 'Cancel',
    no_affiliations: 'There are no affiliations.',
    errors: {
      remove_affiliation_error_message:
        'An error occurred. It was not possible to remove the affiliation.',
      update_affiliation_error_message:
        'An error occurred. It was not possible to change the affiliation type.',
    },
  },
  user_applications: {
    entity_filter: 'Entity',
    search_input_placeholder: 'Search applications',
    name_header: 'Name',
    category_header: 'Category',
    state_header: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    disabled_state_tooltip: 'You do not have permissions to change the application state',
    no_applications: 'There are no applications.',
    enable_application_dialog_title: 'Are you sure you want enable the application {{name}}?',
    enable_application_dialog_message:
      'By enabling the application, it will be available to {{name}}.',
    enable_application_for_entity_dialog_message:
      'By enabling the application, it will be available to {{name}} in the entity {{entity}}.',
    enable_application_dialog_action_yes_button: 'Yes, enable',
    enable_application_dialog_action_no_button: 'Cancel',
    disable_application_dialog_title: 'Are you sure you want disable the application {{name}}?',
    disable_application_dialog_message:
      'By disabling the application, it will no longer be available to {{name}}.',
    disable_application_for_entity_dialog_message:
      'By disabling the application, it will no longer be available to {{name}} in the entity {{entity}}.',
    disable_application_dialog_action_yes_button: 'Yes, disable',
    disable_application_dialog_action_no_button: 'Cancel',
    no_affiliations_info_message:
      "The user has no affiliation with any entity. Therefore, it is not possible to consult the user's applications.",
    errors: {
      enable_application_error_message:
        'An error occurred. It was not possible to enable the application.',
      disable_application_error_message:
        'An error occurred. It was not possible to disable the application.',
      update_state_application_error_message:
        'An error occurred. It was not possible to update the state of the application.',
    },
  },
  user_form: {
    title_create_senior: 'Add senior',
    title_current_user_profile: 'Profile',
    delete_button: 'Delete this profile',
    delete_caregiver_dialog_title: 'Delete profile of caregiver {{name}}?',
    delete_caregiver_dialog_content:
      "By deleting this profile, it will be permanently deleted from the list of caregivers and {{name}}'s affiliation with the entity {{entity}} will be removed.",
    delete_senior_dialog_title:
      'Are you sure you want to delete "{{name}}" from the seniors\' list?',
    delete_senior_dialog_content: 'The senior will be marked as inactive.',
    delete_caregiver_invite_title: 'Cancel the invitation for {{name}}?',
    delete_caregiver_invite_content:
      'By cancelling, {{name}} will not be able to accept the invitation.',
    entity_input: 'Entity',
    full_name_input: 'Full name',
    display_name_input: 'Name',
    state_input: 'State {{ role }}',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    gender_input: 'Gender',
    gender_placeholder_input: 'Select a gender',
    birthday_input: 'Birthday',
    date_placeholder_input: 'Select a date',
    admission_date_input: 'Admission date',
    departure_date_input: 'Departure date',
    address_input: 'Address',
    address_placeholder_input: 'Write your address',
    religion_input: 'Religion',
    religion_placeholder_input: 'Select a religion',
    profession_input: 'Professional field',
    profession_description_input: 'Profession/Occupation',
    social_network_city_input: 'Social Location',
    language_input: 'Language',
    language_placeholder_input: 'Select a language',
    region_input: 'Region',
    region_placeholder_input: 'Select a region',
    email_input: 'Email address',
    email_placeholder_input: 'Add email',
    mobile_phone_number_input: 'Mobile phone number',
    mobile_phone_number_placeholder_input: 'Add a cell phone number',
    mobile_and_landline_phone_number_input: 'Mobile/telephone number',
    tax_payer_number_input: 'Tax identification number - TIN',
    tax_payer_number_input_placeholder: 'Add a tax identification number',
    input_disabled_by_unknown_region: 'You need to add the "Region" to edit this field',
    patient_number_input: 'Patient number',
    patient_number_input_placeholder: 'Add a patient number',
    social_security_number_input: 'Social Security number - SSN',
    social_security_number_input_placeholder: 'Add a social security number',
    room_input: 'Room',
    room_placeholder_input: 'The number of the room where the senior is staying in',
    social_responses_input: 'Care services',
    social_responses_placeholder_input: "Select your institution's care services",
    profile_picture_input: 'Profile Picture',
    process_number_input: 'Process number',
    process_number_input_placeholder: 'Add a process number',
    recurring_fee_input: 'Monthly fee',
    recurring_fee_input_placeholder: '500',
    marital_status_input: 'Marital status',
    marital_status_input_placeholder: 'Select a marital status',
    aditional_info_input: 'Additional information',
    additional_info_input_placeholder: 'Write...',
    education_level_input: 'Education level',
    education_level_input_placeholder: 'Select an education level',
    citizen_card_input: 'Citizen Card Number - ID',
    citizen_card_input_placeholder: 'Add a citizen card number',
    cancel_button: 'Cancel',
    save_button: 'Save',
    saving_button: 'Saving...',
    deleting_button: 'Deleting...',
    create_senior_dialog_title: 'Save creation of senior "{{name}}"?',
    create_senior_dialog_message: 'By saving, the new senior will be added to the seniors list.',
    edit_senior_dialog_title: 'Save changes to the profile of the senior "{{name}}"?',
    edit_senior_dialog_message:
      'By saving, the senior will be updated with the newly entered information.',
    edit_caregiver_dialog_title: 'Save changes to the profile of the caregiver "{{name}}"?',
    edit_caregiver_dialog_message:
      'By saving, the caregiver will be updated with the newly entered information.',
    edit_caregiver_affiliation_type_dialog_title:
      'Save changes to the affiliation type of the caregiver {{name}}?',
    edit_caregiver_affiliation_type_dialog_message:
      'By saving, the affiliation type of the caregiver will be updated with the newly entered information.',
    edit_current_user_profile_dialog_title: 'Save changes to your profile?',
    edit_current_user_profile_dialog_message:
      'By saving, your profile will be updated with the newly entered information.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Save',
    senior_router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the profile of the senior "{{name}}"?',
    senior_router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the senior "{{name}}"?',
    senior_router_prompt_create_message:
      'By exiting, the senior will not be saved and all information entered in it will be lost.',
    senior_router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the senior profile.',
    caregiver_edit_router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the profile of the caregiver "{{name}}"?',
    caregiver_edit_router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the caregiver profile.',
    caregiver_edit_affiliation_type_router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the affiliation type of the caregiver {{name}}?',
    caregiver_edit_affiliation_type_router_prompt_edit_message:
      "By exiting, you will lose all newly entered changes to the caregiver's affiliation type.",
    current_user_router_prompt_edit_title:
      'Are you sure you want exit without saving changes to your profile?',
    current_user_router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to your profile.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    senior_create_cancel_toast: 'The creation of the new senior was canceled.',
    senior_edit_cancel_toast: 'The edition of the senior "{{name}}" was canceled.',
    caregiver_edit_cancel_toast: 'The edition of the caregiver "{{name}}" was canceled.',
    current_user_edit_cancel_toast: 'The edition of your profile was canceled.',
    caregiver_edit_affiliation_type_cancel_toast:
      'The edition of the affiliation type for the caregiver {{name}} was cancelled.',
    current_user_edit_email_not_allowed_tooltip_title:
      'It is currently not possible to change your email address.',
    caregiver_edit_email_not_allowed_tooltip_title:
      'It is currently not possible to change the email address of the caregiver.',
    current_user_edit_state_not_allowed_tooltip_title:
      'It is currently not possible to change your state.',
    profile_picture_info_message: 'For best resolution, the image should be JPG/JPEG.',
    display_name_current_user_info_message:
      'In this field, indicate the name by which you are called in everyday life. This is the name that other users will see on the system when interacting with you.',
    display_name_other_user_info_message:
      'In this field, indicate the name by which the user is called in everyday life. This is the name that family members, friends, and other caregivers will see on the system when interacting with the user.',
    roles_ids_input: 'Permissions',
    roles_ids_placeholder_input: 'Select permissions',
    input_placeholder: 'Escreva um {{ type }}',
    not_specified: 'Not specified',
    input_read_only_tooltip: 'You do not have permissions to edit the profile information',
    quick_access_code_input: 'Quick Access Code',
    quick_access_code_redefine_button: 'Redefine',
    quick_access_code_disabled:
      'There is already a quick access code. To change click on "Redefine"',
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_senior_success_message: 'The senior "{{name}}" was created with success!',
      edit_senior_success_message: "The changes on the senior's profile were saved.",
      edit_caregiver_success_message: "The changes on the caregiver's profile were saved.",
      edit_caregiver_roles_ids_success_message: "The changes to the caregiver's roles were saved.",
      edit_current_user_success_message: 'The changes on your profile were saved.',
      edit_current_user_app_language_to_chosen_language_success_message:
        "The app's language was updated to {{language}}.",
      edit_current_user_app_language_to_browser_language_success_message:
        "The app's language was updated to your browser language, {{language}}.",
      edit_current_user_app_language_to_default_language_success_message:
        "The app's language was updated to the default language, {{language}}.",
      success_delete_senior: 'Senior {{name}} deleted.',
      success_delete_caregiver: 'Caregiver {{name}} deleted.',
      success_delete_invite: 'Invitation for {{name}} canceled.',
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_senior_button: 'Error while deleting the senior',
      error_on_delete_caregiver_button: 'Error while deleting the caregiver',
      error_on_delete_invite: 'Error while canceling the invitation',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the profile',
      caregiver_edit_affiliation_type_form_validation_invalid_field:
        'Fill in this field to complete editing the caregiver affiliation type',
      form_validation_invalid_phone_number: 'The mobile phone number provided is not valid',
      form_validation_not_mobile_phone_number:
        'The number provided does not match a mobile phone number',
      form_validation_invalid_email: 'The email address provided is not valid',
      form_validation_invalid_number:
        'According {{region}} region number should have {{digits}} characters',
      form_validation_invalid_room_name: 'The room name provided is not valid',
      form_validation_room_maximum_characters:
        'The room name must have a maximum of {{max}} characters',
      form_validation_invalid_display_name_maximum_characters:
        'The name must have less than {{max}} characters',
      create_active_senior_licenses_number_exceeded_error:
        'It was not possible to create an active senior. The entity has already reached the maximum number of active seniors for the number of active licenses.',
      activate_senior_licenses_number_exceeded_error:
        'It was not possible to activate the senior. The entity has already reached the maximum number of active seniors for the number of active licenses.',
      form_validation_invalid_quick_access_code:
        'The quick access code must have {{digits}} digits',
      form_validation_process_number_max_chars:
        'The process number must have less than {{max}} characters',
      form_validation_recurring_fee_max_chars:
        'The monthly fee must have less than {{max}} characters',
      form_validation_aditional_info_max_chars:
        'The additional information must have less than {{max}} characters',
    },
  },
  user_attachments: {
    list: {
      headers: {
        name: 'Name',
        description: 'Description',
        author: 'Added by',
        date: 'Date',
        size: 'Size',
      },
      actions: {
        edit: 'Edit',
        delete: 'Delete',
        download: 'Download',
      },
    },
    empty: {
      title: 'There are no attachments registered yet.',
      subtitle: 'Press add to add an attachment.',
    },
    modals: {
      create: {
        title: 'Add attachments',
      },
      edit: {
        title: 'Edit attachment',
        name: 'Name',
        description: 'Description',
        errors: {
          name_required: 'The attachment name is required',
          name_max_length: 'The attachment name must be less than {{max}} characters',
          description_max_length: 'The attachment description must be less than {{max}} characters',
        },
      },
      delete: {
        title: 'Are you sure you want to delete the attachment<br/>"{{name}}"?',
        description: 'By deleting, the attachment will be permanently deleted.',
      },
    },
    success: {
      create: 'Attachments added successfully',
      edit: 'Attachment edited successfully',
      delete: 'Attachment deleted successfully',
    },
    error: {
      create: 'Error while adding attachments',
      edit: 'Error while editing the attachment',
      delete: 'Error while deleting the attachment',
      download: 'Error while downloading the attachment',
    },
    access_denied: {
      create: 'You do not have permission to add attachments',
      edit: 'You do not have permission to edit attachments',
      delete: 'You do not have permission to delete attachments',
    },
  },
  entity_services: {
    root: {
      title: 'Services',
      add_button: 'Create service',
      search_input_placeholder: 'Search services',
      name_header: 'Name',
      display_order_header: 'Order',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      no_services: 'There are no services configured.',
      deleted_service_dialog_title: 'Service "{{name}}" deleted',
      deleted_service_dialog_message1: '"{{name}}" was deleted.',
      deleted_service_dialog_message2:
        'The service is no longer available on the {{caregiversAppName}} application and on {{seniorsDeviceName}}.',
      deleted_service_dialog_ok_button: 'Ok',
    },
    edit: {
      title_create: 'Create service',
      title_edit: 'Edit service',
      title_view: 'View service',
      exit_button: 'Exit',
      save_button: 'Save',
      saving_button: 'Saving...',
      title_input: 'Name',
      state_input: 'State',
      phone_number_input: 'Phone number',
      phone_number_use_general_number: 'Use general number',
      phone_number_use_specific_number: 'Use specific number',
      display_order_input: 'Order',
      description_input: 'Description',
      details_input: 'Details',
      attach_document_input: 'Add PDF document',
      document_informative_note_input: 'Message about contract information',
      icon_input: 'Icon',
      image_input: 'Image',
      button_preview: 'Button preview',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      number_input: 'Number',
      upload: 'Attach',
      remove_document: 'Remove',
      view_document: 'View',
      image_info:
        'The image must be of type JPG/JPEG. For better results use the resolution of 292x288px.',
      delete_button: 'Delete service',
      deleting_button: 'Deleting...',
      delete_dialog_title: 'Delete service "{{name}}"?',
      delete_dialog_content:
        "By deleting this service, all users of the {{caregiversAppName}} application and {{seniorsDeviceName}} will lose access to it. In addition, the service will also be permanently deleted from the entity's list of services.",
      delete_dialog_action_yes_button: 'Yes, delete',
      delete_dialog_action_no_button: 'Cancel',
      router_prompt_edit_title:
        'Are you sure you want exit without saving changes to the service details of "{{name}}"?',
      router_prompt_create_title:
        'Are you sure you want exit without saving the creation of the service "{{name}}"?',
      router_prompt_create_message:
        'By exiting, the service will not be saved and all information entered in it will be lost.',
      router_prompt_edit_message:
        'By exiting, you will lose all newly entered changes to the service details.',
      router_prompt_action_cancel_button: 'Cancel',
      router_prompt_action_ok_button: 'Yes, exit',
      create_service_dialog_title: 'Save creation of service "{{name}}"?',
      create_service_dialog_message:
        'By saving, the new service will be added to the services list.',
      edit_service_dialog_title: 'Save changes to service details "{{name}}"?',
      edit_service_dialog_message:
        'By saving, the service will be updated with the newly entered information.',
      save_service_dialog_action_cancel_button: 'Cancel',
      save_service_dialog_action_save_button: 'Save',
      service_create_cancel_toast: 'The creation of the new service was canceled.',
      service_edit_cancel_toast: 'The edition of the service "{{name}}" was canceled.',
      errors: {
        error_on_save_button: 'Error while saving',
        error_on_delete_button: 'Error while deleting',
        edit_form_validation_invalid_field: 'Fill in this field to complete editing the service',
        create_form_validation_invalid_field:
          'Fill in this field to complete the creation of the service',
        create_form_validation_invalid_icon: 'Pick an icon to complete the creation of the service',
        create_form_validation_invalid_image:
          'Upload an image to complete the creation of the service',
        form_validation_invalid_phone_number: 'The phone number provided is not valid',
        service_does_not_exist_error: 'It was not possible to get the service you tried to access.',
      },
      success: {
        save_success_button: 'Saved!',
        delete_success_button: 'Deleted!',
        create_success_message: 'The service "{{name}}" was created with success!',
        edit_success_message: "The changes on the service's details were saved.",
      },
    },
  },
  applications: {
    system_administrator: {
      root: {
        title: 'Applications',
        search_input_placeholder: 'Search applications',
        name_header: 'Name',
        category_header: 'Category',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_applications: 'There are no applications.',
        add_button: 'Add application',
      },
      edit: {
        entities: {
          search_input_placeholder: 'Search entities',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_entities: 'There are no entities.',
          enable_application_dialog_message:
            'By enabling the application, it will be available to the entity {{name}}.',
          enable_application_dialog_action_yes_button: 'Yes, enable',
          enable_application_dialog_action_no_button: 'Cancel',
          disable_application_dialog_message:
            'By disabling the application, it will no longer be available to the entity {{name}}.',
          disable_application_dialog_action_yes_button: 'Yes, disable',
          disable_application_dialog_action_no_button: 'Cancel',
          errors: {
            enable_application_error_message:
              'An error occurred. It was not possible to enable the application.',
            disable_application_error_message:
              'An error occurred. It was not possible to disable the application.',
            update_state_application_error_message:
              'An error occurred. It was not possible to update the state of the application.',
          },
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Search applications',
        name_header: 'Name',
        category_header: 'Category',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_applications: 'There are no applications.',
        enable_application_dialog_message:
          'By enabling the application, it will be available to all users in the entity {{name}}.',
        enable_application_dialog_action_yes_button: 'Yes, enable',
        enable_application_dialog_action_no_button: 'Cancel',
        disable_application_dialog_message:
          'By disabling the application, it will no longer be available to all users in the entity {{name}}.',
        disable_application_dialog_action_yes_button: 'Yes, disable',
        disable_application_dialog_action_no_button: 'Cancel',
        errors: {
          enable_application_error_message:
            'An error occurred. It was not possible to enable the application.',
          disable_application_error_message:
            'An error occurred. It was not possible to disable the application.',
          update_state_application_error_message:
            'An error occurred. It was not possible to update the state of the application.',
        },
      },
      edit: {
        users: {
          search_input_placeholder: 'Search users',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_users: 'There are no users.',
          enable_application_dialog_message:
            'By enabling the application, it will be available to {{name}}.',
          enable_application_dialog_action_yes_button: 'Yes, enable',
          enable_application_dialog_action_no_button: 'Cancel',
          disable_application_dialog_message:
            'By disabling the application, it will no longer be available to {{name}}.',
          disable_application_dialog_action_yes_button: 'Yes, disable',
          disable_application_dialog_action_no_button: 'Cancel',
          errors: {
            enable_application_error_message:
              'An error occurred. It was not possible to enable the application.',
            disable_application_error_message:
              'An error occurred. It was not possible to disable the application.',
            update_state_application_error_message:
              'An error occurred. It was not possible to update the state of the application.',
          },
        },
      },
    },
  },
  application_tabs: {
    tabs: {
      details: 'Details',
      entities: 'Entities',
      users: 'Users',
    },
    errors: {
      application_does_not_exist_error:
        'It was not possible to get the application you tried to access.',
    },
  },
  application_form: {
    name_input: 'Name',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    description_input: 'Description',
    is_system_application_input: 'System application',
    is_system_application_value_yes: 'Yes',
    is_system_application_value_no: 'No',
    available_for_user_types_input: 'Types of users for which the application is available',
    icon_input: 'Icon',
    picture_input: 'Picture',
    category_input: 'Category',
    activity_type_input: 'Activity type',
    windows_key_input: 'Windows key',
    android_key_input: 'Android key',
    input_disabled_tooltip: 'You do not have permissions to edit the application',
  },
  devices: {
    root: {
      search_input_placeholder: 'Search devices',
      identifier_header: 'Identifier',
      type_header: 'Type',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      user_header: 'User',
      no_devices: 'There are no devices.',
      add_button: 'Add device',
    },
  },
  device_tabs: {
    device_title: 'Device "{{name}}"',
    tabs: {
      details: 'Details',
      health: 'Health',
      information: 'Information',
    },
    errors: {
      device_does_not_exist_error: 'It was not possible to get the device you tried to access.',
    },
  },
  user_cards: {
    root: {
      title: 'Cards',
      search_input_placeholder: 'Search cards',
      name_header: 'Name',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      user_header: 'User',
      no_cards: 'There are no cards.',
      add_button: 'Add card',
      deleted_card_dialog_title: 'Card "{{name}}" deleted',
      deleted_card_dialog_message: 'The card no longer exists in the system.',
      deleted_card_dialog_ok_button: 'Ok',
    },
    edit: {
      errors: {
        card_does_not_exist_error: 'It was not possible to get the card you tried to access.',
      },
    },
  },
  gdpr_erasure_requests: {
    root: {
      title: 'Right to Erasure (GDPR)',
      add_button: 'Request erasure',
      no_requests: 'There are no erasure requests.',
      status_header: 'Status',
      requested_at_header: 'Requested at',
      completed_at_header: 'Completed at',
      error_header: 'Error',
      user_id_hash_header: 'User hash',
      user_email_hash_header: 'Email hash',
      status_processing: 'Processing',
      status_completed: 'Completed',
      status_failed: 'Failed',
    },
    create_modal: {
      title: 'Request personal data erasure',
      email_label: "User's email",
      email_placeholder: 'user@example.com',
      email_required: 'Email is required.',
      email_invalid: 'Enter a valid email.',
      cancel_button: 'Cancel',
      submit_button: 'Continue',
      submitting_button: 'Submitting...',
      confirm_title: 'Confirm data erasure',
      confirm_description:
        'This will permanently erase/anonymize the personal data of the user associated with this email.',
      confirm_cannot_be_undone: 'This action is irreversible and cannot be undone.',
      confirm_button: 'Erase permanently',
      cancel_confirm_button: 'Cancel',
    },
    errors: {
      forbidden: 'You do not have permission to perform this action.',
      user_not_found: 'There is no user with that email.',
      unknown: 'An error occurred while processing the request. Please try again.',
    },
    success: {
      request_created: 'Erasure request created successfully.',
    },
  },
  entity_photos: {
    root: {
      add_button: 'Add',
      deleted_photo_dialog_title: 'Photo deleted',
      deleted_photo_dialog_message:
        'The photo is no longer available on the screensaver of the {{brand}} computer.',
      deleted_photo_dialog_ok_button: 'Ok',
      entity_photos_only_for_computer_info_message:
        'The added photos can be viewed on Sioslife computers and tablets.',
    },
    edit: {
      title_create: 'Create photos',
      title_edit: 'Edit photo',
      photo_input: 'Photo',
      photos_input: 'Photos',
      description_input: 'Description',
      exit_button: 'Exit',
      save_button: 'Save',
      saving_button: 'Saving...',
      photos_create_cancel_toast: 'The creation of the new photos was canceled.',
      photo_edit_cancel_toast: 'The edition of the photo was canceled.',
      delete_button: 'Delete photo',
      deleting_button: 'Deleting...',
      delete_dialog_title: 'Are you sure you want delete photo?',
      delete_dialog_content:
        'By deleting the photo, it will no longer appear on the screensaver of the {{brand}} computer and it will be permanently deleted.',
      delete_dialog_action_yes_button: 'Yes, delete',
      delete_dialog_action_no_button: 'Cancel',
      router_prompt_edit_title: 'Are you sure you want exit without saving changes to the photo?',
      router_prompt_edit_message:
        'By exiting, you will lose all newly entered changes to the photo.',
      router_prompt_create_title:
        'Are you sure you want exit without saving the creation of the photos?',
      router_prompt_create_message:
        'By exiting, the photos will not be saved and all information entered in it will be lost.',
      router_prompt_action_cancel_button: 'Cancel',
      router_prompt_action_ok_button: 'Yes, exit',
      photos_info: 'The photos must be of type JPG/JPEG.',
      choose_photos_button: 'Choose photos',
      input_disabled_tooltip: 'You do not have permissions to edit the photo details',
      errors: {
        error_on_save_button: 'Error while saving',
        error_on_delete_button: 'Error while deleting',
        photo_does_not_exist_error: 'It was not possible to get the photo you tried to access.',
        create_form_validation_invalid_field:
          'Fill in this field to complete the creation of the photos',
        form_validation_invalid_description_maximum_characters:
          'The description must have less than {{max}} characters',
      },
      success: {
        save_success_button: 'Saved!',
        delete_success_button: 'Deleted!',
        create_success_message: 'The photos were created with success!',
        edit_success_message: 'The changes on the photo were saved.',
      },
      warnings: {
        partial_create_success_message:
          'Some photos were created with success! However, it was not possible to create {{number}} photo(s): {{files}}.',
      },
    },
  },
  entities: {
    root: {
      title: 'Entities',
      search_input_placeholder: 'Search entities',
      name_header: 'Name',
      type_header: 'Type',
      state_header: 'State',
      state_value_true: 'Active',
      state_value_false: 'Inactive',
      no_entities: 'There are no entities.',
      add_button: 'Add entity',
      deleted_entity_dialog_title: 'Entity "{{name}}" deleted',
      deleted_entity_dialog_message: 'The entity no longer exists in the system.',
      deleted_entity_dialog_ok_button: 'Ok',
    },
    edit: {
      tabs: {
        details: 'Details',
        seniors: 'Seniors',
        professional_caregivers: 'Professionals',
        applications: 'Applications',
        devices: 'Devices',
        user_cards: 'Cards',
        activity: 'Activity',
        statistics: 'Statistics',
        licenses: 'Licenses',
        playlists: 'Playlists',
      },
      seniors: {
        search_input_placeholder: 'Search seniors',
        name_header: 'Name',
        gender_header: 'Gender',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_seniors: 'There are no seniors.',
        enable_senior_dialog_title: 'Enable the senior {{name}}?',
        enable_senior_dialog_message:
          'By enabling the senior, they will be able to enjoy the {{brand}} system.',
        enable_senior_dialog_action_yes_button: 'Yes, enable',
        enable_senior_dialog_action_no_button: 'Cancel',
        disable_senior_dialog_title: 'Disable the senior {{name}}?',
        disable_senior_dialog_message:
          'By disabling the senior, they will no longer be able to enjoy the {{brand}} system.',
        disable_senior_dialog_action_yes_button: 'Yes disable',
        disable_senior_dialog_action_no_button: 'Cancel',
        errors: {
          enable_senior_error_message:
            'An error occurred. It was not possible to enable the senior.',
          disable_senior_error_message:
            'An error occurred. It was not possible to disable the senior.',
          update_state_senior_error_message:
            'An error occurred. It was not possible to update the state of the senior.',
        },
      },
      professional_caregivers: {
        search_input_placeholder: 'Search professionals',
        invite_button: 'Invite professional',
        name_header: 'Name',
        email_header: 'Email',
        affiliation_type_header: 'Type',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_professional_caregivers: 'There are no professionals.',
      },
      applications: {
        search_input_placeholder: 'Search applications',
        name_header: 'Name',
        category_header: 'Category',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_applications: 'There are no applications.',
        enable_application_dialog_message:
          'By enabling the application, it will be available to the entity {{name}}.',
        enable_application_dialog_action_yes_button: 'Yes, enable',
        enable_application_dialog_action_no_button: 'Cancel',
        disable_application_dialog_message:
          'By disabling the application, it will no longer be available to the entity {{name}}.',
        disable_application_dialog_action_yes_button: 'Yes, disable',
        disable_application_dialog_action_no_button: 'Cancel',
        errors: {
          enable_application_error_message:
            'An error occurred. It was not possible to enable the application.',
          disable_application_error_message:
            'An error occurred. It was not possible to disable the application.',
          update_state_application_error_message:
            'An error occurred. It was not possible to update the state of the application.',
        },
      },
      devices: {
        search_input_placeholder: 'Search devices',
        identifier_header: 'Identifier',
        type_header: 'Type',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        user_header: 'User',
        no_devices: 'There are no devices.',
      },
      user_cards: {
        search_input_placeholder: 'Search cards',
        name_header: 'Name',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        user_header: 'User',
        no_cards: 'There are no cards.',
        front_card_design_button: 'Design Front of Card',
        back_card_design_button: 'Design Back of Card',
      },
      playlists: {
        search_input_placeholder: 'Search playlists',
        type_filter: 'Type',
        name_header: 'Name',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_playlist: 'There are no playlists.',
        enable_playlist_dialog_title: 'Enable the playlist "{{name}}"?',
        enable_playlist_dialog_message:
          'By enabling the playlist, it will be available to the entity {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Yes, enable',
        enable_playlist_dialog_action_no_button: 'Cancel',
        disable_playlist_dialog_title: 'Disable the playlist "{{name}}"?',
        disable_playlist_dialog_message:
          'By disabling the playlist, it will no longer be available to the entity {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Yes, disable',
        disable_playlist_dialog_action_no_button: 'Cancel',
        errors: {
          enable_playlist_error_message:
            'An error occurred. It was not possible to enable the playlist.',
          disable_playlist_error_message:
            'An error occurred. It was not possible to disable the playlist.',
          update_state_playlist_error_message:
            'An error occurred. It was not possible to update the state of the playlist.',
        },
      },
      playlist_items: {
        title: 'Playlist "{{name}}"',
        search_input_placeholder: 'Search items',
        title_header: 'Title',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_items: 'There are no items in the playlist.',
        enable_item_dialog_title: 'Enable the item "{{name}}" in the playlist?',
        enable_item_dialog_message:
          'By enabling the item in the playlist, it will be available to the entity {{name}}.',
        enable_item_dialog_action_yes_button: 'Yes, enable',
        enable_item_dialog_action_no_button: 'Cancel',
        disable_item_dialog_title: 'Disable the item "{{name}}" in the playlist?',
        disable_item_dialog_message:
          'By disabling the item in the playlist, it will no longer be available to the entity {{name}}.',
        disable_item_dialog_action_yes_button: 'Yes, disable',
        disable_item_dialog_action_no_button: 'Cancel',
        errors: {
          enable_item_error_message:
            'An error occurred. It was not possible to enable the item in the playlist.',
          disable_item_error_message:
            'An error occurred. It was not possible to disable the item in the playlist.',
          update_state_item_error_message:
            'An error occurred. It was not possible to update the state of the item in the playlist.',
          playlist_does_not_exist_error:
            'It was not possible to get the playlist you tried to access.',
        },
      },
      licenses: {
        number_of_monthly_active_licenses_not_defined: 'Not defined',
        number_of_monthly_active_licenses_title: 'Monthly active licenses',
        save_button: 'Save',
        saving_button: 'Saving...',
        edit_dialog_title: 'Save changes to the number of monthly active licenses?',
        edit_dialog_content:
          'By saving, the number of monthly active licenses will be updated to {{total}} for the entity {{entity}}.',
        edit_dialog_action_cancel_button: 'Cancel',
        edit_dialog_action_save_button: 'Save',
        router_prompt_title:
          'Are you sure you want exit without saving changes to the number of monthly active licenses?',
        router_prompt_message:
          'By exiting, you will lose all recently entered changes to the number of monthly active licenses for the entity {{entity}}.',
        router_prompt_action_cancel_button: 'Cancel',
        router_prompt_action_ok_button: 'Sim, exit',
        number_of_monthly_active_licenses_edit_cancel_toast:
          'The edition of the number of monthly active licenses was canceled.',
        success: {
          save_success_button: 'Saved!',
        },
        errors: {
          error_on_save_button: 'Error while saving',
          edit_form_validation_invalid_field:
            'Fill in this field to complete editing the number of monthly active licenses',
          form_validation_invalid_number_of_monthly_active_licenses:
            'The number of monthly active licenses is invalid',
        },
      },
      errors: {
        entity_does_not_exist_error: 'It was not possible to get the entity you tried to access.',
      },
    },
  },
  affiliated_entity_profile: {
    errors: {
      entity_does_not_exist_error: 'It was not possible to get the entity you tried to access.',
    },
  },
  section_tabs: {exit_button: 'Exit'},
  profession_input: {no_options: 'No options', loading: 'Loading...'},
  social_network_city_input: {no_options: 'No options', loading: 'Loading...'},
  user_menu: {
    logout: 'Logout',
    profile: 'Profile',
    settings: 'Settings',
    office: 'Management area',
    dailyCareLog: 'Daily care log',
    errors: {logout_error_message: 'An error occurred. It was not possible to logout.'},
  },
  error_box: {
    title: 'Unable to perform the action.',
    description:
      'Sorry, there was an error trying to perform the action. Please, contact the Support team with the details provided.',
    details_button: 'See details',
  },
  country_select: {
    title: 'Country code',
    no_options: 'No options',
  },
  tablet_button: {
    empty_title: '(Button title)',
  },
  icons_picker: {
    title: 'Icons Picker',
    cancel_button: 'Cancel',
    confirm_button: 'Confirm',
  },
  image_cropper_dialog: {
    title: 'Prepare image',
    zoom: 'Zoom',
    rotation: 'Rotation',
    save_button: 'Save',
    cancel_button: 'Cancel',
  },
  loading_content: {
    loading: 'Loading...',
  },
  pdf_viewer: {
    error: 'Unable to show the PDF.',
  },
  activity_card: {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This week',
    this_month: 'This month',
    previous_month: 'Last month',
    past: 'Past',
    entity: 'Entity',
    device: 'Device',
    duration: 'Duration',
    end_type: 'End type',
    participants: 'Participants',
    application: 'Application',
    application_battle_with: 'with',
    level_on_app: 'Level {{level}} in the application',
    missed_call: 'tried to call but the call was not answered.',
    rejected_call: 'tried to call but the call was rejected.',
    call_started: 'called',
    drawing_sent: 'Drawing sent to',
    drawing_saved: 'Drawing shared with',
    drawing_shared: 'Drawing shared in Family Album of',
    painting_sent: 'Painting sent to',
    painting_saved: 'Painting shared with',
    painting_shared: 'Painting shared in Family Album of',
    and: 'and',
    photo_sent: 'Photo sent to',
    photo_saved: 'Photo shared with',
    photo_shared: 'Photo shared in Family Album of',
    camera_used: 'Camera used',
    response: 'Response',
    video_message_sent_to: 'Video message sent to',
    video_message_sent_by: 'Video message sent by',
    video_sent: 'Video sent to',
    video_saved: 'Video shared with',
    video_shared: 'Video shared in Family Album of',
    currently_happening: 'Happening at this moment',
  },
  notification_card: {
    open_invite: 'Open invitation',
    open_occurrences: 'Go to occurrence',
    relationship_invite: {
      message_sender: '{{sender}} sent you an invitation to add {{target}} to your contacts list.',
      message_sender_same_as_target:
        '{{sender}} sent you an invitation to add them to your contacts list.',
      message_sender_unknown: 'You received an invitation to add {{target}} to your contacts list.',
    },
    affiliation_invite: {
      message_sender: '{{sender}} sent you an invitation to affiliate with the entity {{target}}.',
      message_sender_same_as_target: '{{sender}} sent you an invitation to affiliate.',
      message_sender_unknown: 'You received an invitation to affiliate with the entity {{target}}.',
    },
    user_signed_in: '{{name}} signed in {{brand}}.',
    photo_sent: '{{name}} sent you a photo.',
    photo_shared: '{{name}} shared a photo.',
    drawing_sent: '{{name}} sent you a drawing.',
    drawing_shared: '{{name}} shared a drawing.',
    painting_sent: '{{name}} sent you a painting.',
    painting_shared: '{{name}} shared a painting.',
    video_sent: '{{name}} sent you a video.',
    video_shared: '{{name}} shared a video.',
    without_license: '{{name}} does not have a license to use {{brand}} systems.',
    missed_call: '{{name}} tried to call you but got no answer.',
    video_message: '{{name}} sent you a video message.',
    sos_request: 'SOS help request from {{name}}. Please, confirm that everything is fine.',
    reminder_response: {
      message: '{{name}} {{response}} to the reminder "{{reminder}}".',
      responses: {
        positive: 'replied "Yes"',
        negative: 'replied "No"',
        not_responding: 'did not answer',
      },
    },
    relationship_invite_request_result: {
      message_accepted: '{{name}} accepted {{target}} as a contact.',
      message_declined: '{{name}} did not accept {{target}} as a contact.',
    },
    caregiver_administrator_role_request_result: {
      message_accepted: '{{name}} accepted {{target}} as Administrator.',
      message_declined: '{{name}} did not accept {{target}} as Administrator.',
    },
    relationship_role_change: {
      message_senior_myself: '{{name}} defined your profile as {{role}} of their {{brand}}.',
      message_senior_other: '{{name}} defined {{target}} as {{role}} of their {{brand}}.',
      message_caregiver_myself:
        "{{name}} defined your profile as {{role}} of {{senior}}'s {{brand}}.",
      message_caregiver_other: "{{name}} defined {{target}} as {{role}} of {{senior}}'s {{brand}}.",
      message_unknown_myself: "Your profile was defined as {{role}} of {{senior}}'s {{brand}}.",
      message_unknown_other: "{{target}} was defined as {{role}} of {{senior}}'s {{brand}}.",
    },
    personal_data_sharing_preferences_change: {
      message: '{{name}} changed the shared data settings of their {{brand}}.',
      current_data_settings_title: 'The current settings are:',
      can_access_profile_data: 'Profile data',
      can_access_contacts: 'Contacts',
      can_access_applications: 'Applications',
      can_access_multimedia: 'Multimedia',
      can_access_reminders: 'Reminders',
      can_access_activity_feed: 'Activity feed',
    },
    relationship_end: {
      message_myself:
        '{{name}} removed your profile from their contacts list. From now on you will not be able to communicate through {{brand}} products.',
      message_other:
        '{{name}} removed {{target}} from their contacts list. From now on {{target}} will not be able to communicate with {{name}} through {{brand}} products.',
    },
    media_processing_status: {
      video_message: {
        [ProcessStatus.Done]: 'The video message was sent to {{name}}.',
        [ProcessStatus.Failed]:
          'An error occurred while sending the video message. {{name}} will not receive the message.',
      },
      video_shared: {
        [ProcessStatus.Done]: 'The video was shared with {{name}}.',
        [ProcessStatus.Failed]:
          'An error occurred while sharing the video. {{name}} will not receive the video.',
      },
      video_saved: {
        [ProcessStatus.Done]: 'The video was saved successfully.',
        [ProcessStatus.Failed]: 'An error occurred while saving the video.',
      },
      photo_shared: {
        [ProcessStatus.Done]: 'The photo was shared with {{name}}.',
        [ProcessStatus.Failed]:
          'An error occurred while sharing the photo. {{name}} will not receive the photo.',
      },
      photo_saved: {
        [ProcessStatus.Done]: 'The photo was saved successfully.',
        [ProcessStatus.Failed]: 'An error occurred while saving the photo.',
      },
      unknown: {
        [ProcessStatus.Done]: 'The media was saved successfully.',
        [ProcessStatus.Failed]: 'An error occurred while saving the media.',
      },
    },
    media_empty: '{{name}} asked you to share photos and videos.',
    new_occurrence: 'New occurrence created "{{name}}".',
    new_occurrence_patient: 'New occurrence created "{{name}}" for {{patient}}.',
    new_occurrence_assigned: '{{registered}} pointed you as responsible of occurrence "{{name}}".',
    new_occurrence_assigned_patient:
      '{{registered}} pointed you as responsible of occurrence "{{name}}" of patient {{patient}}.',
  },
  section_list: {
    refresh: 'Refresh',
    no_more_data: 'No more data',
    without_data: 'There are no data',
    today: 'Today',
    old_f: 'Old',
    old_m: 'Old',
  },
  activity_timeline: {title: 'Activity'},
  clipboard_text_field: {
    copy_to_clipboard_button: 'Copy',
    copy_to_clipboard_tooltip_message: 'Copy to clipboard',
    success: {copy_to_clipboard_success_message: 'Text copied to clipboard!'},
  },
  activity_end_types: {
    [ActivityEndType.UserAction]: 'User action',
    [ActivityEndType.SystemTimeout]: 'System timeout',
  },
  device_types: {
    [DeviceType.Handset]: 'Handset',
    [DeviceType.Tablet]: 'Tablet',
    [DeviceType.Desktop]: 'Desktop',
    [DeviceType.Laptop]: 'Laptop',
    [DeviceType.Unknown]: 'Unknown',
  },
  camera_facing: {
    [CameraFacing.Front]: 'Front camera',
    [CameraFacing.Rear]: 'Rear camera',
    [CameraFacing.External]: 'External camera',
    [CameraFacing.Unknown]: 'Unknown camera',
  },
  reminder_response_values: {
    [ReminderResponseValue.Positive]: 'Yes / Ok',
    [ReminderResponseValue.Negative]: 'No',
    [ReminderResponseValue.NotResponding]: 'Did not respond',
  },
  invite_states: {
    [InviteState.Accepted]: 'Accepted',
    [InviteState.Cancelled]: 'Cancelled',
    [InviteState.Open]: 'Opened',
    [InviteState.Pending]: 'Pending',
    [InviteState.Refused]: 'Refused',
    [InviteState.RefusedBySenior]: 'Refused by the senior',
    [InviteState.WaitingForSeniorApproval]: 'Waiting for the senior approval',
    [InviteState.AcceptedBySenior]: 'Accepted by the senior',
    [InviteState.Expired]: 'Expired',
  },
  process_statuses: {
    [ProcessStatus.Done]: 'Done',
    [ProcessStatus.InProgress]: 'In progress',
    [ProcessStatus.ToDo]: 'To do',
    [ProcessStatus.Undefined]: 'Undefined',
    [ProcessStatus.Failed]: 'Failed',
  },
  entity_types: {
    [EntityType.DayCareCenter]: 'Day care center',
    [EntityType.Family]: 'Family',
    [EntityType.Generic]: 'Generic',
    [EntityType.HomeSupport]: 'Home support',
    [EntityType.Institution]: 'Institution',
    [EntityType.InsuranceCompany]: 'Insurance company',
    [EntityType.TechCompany]: 'Tech company',
  },
  care_taks_not_done_reasons: {
    [CareTaskNotDoneReason.SeniorRefusal]: 'Senior refusal',
    [CareTaskNotDoneReason.seniorAbsence]: 'Senior absence',
    [CareTaskNotDoneReason.LackOfMaterial]: 'Lack of material',
    [CareTaskNotDoneReason.SeniorWasNotFeelingWell]: 'Senior was not feeling well',
    [CareTaskNotDoneReason.Other]: 'Other (obs)',
  },
  time_units: {seconds: 's', minutes: 'min', hours: 'hrs'},
  entity_form: {
    exit_button: 'Cancel',
    create_entity_title: 'Create entity',
    picture_input: 'Picture',
    name_input: 'Name of institution',
    name_placeholder_input: 'Insira o nome da instituição',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    description_input: 'Description',
    type_input: 'Type',
    language_input: 'Language',
    language_placeholder_input: 'Select a language',
    region_input: 'Region',
    region_placeholder_input: 'Select a region',
    time_zone_input: 'Time zone',
    time_zone_placeholder_input: 'Select a time zone',
    email_input: 'Email address',
    email_placeholder_input: 'Add email',
    short_name_input: 'Short name',
    short_name_placeholder_input: 'Enter a short name',
    anniversary_input: 'Founding date/Anniversary',
    anniversary_placeholder_input: 'Select a date',
    capacity_input: 'Capacity',
    capacity_placeholder_input: "The institution's reception capacity",
    address_input: 'Address',
    address_placeholder_input: 'Enter an address',
    website_input: 'Website',
    website_placeholder_input: 'Add website',
    number_input: 'Code',
    landline_phone_number_input: 'Phone number',
    mobile_phone_number_input: 'Mobile phone number',
    services_name_input: 'Services name',
    services_name_placeholder_input: 'Nome dos serviços',
    services_phone_number_input: 'Services phone number',
    phone_number_placeholder: 'Insira um número de telefone',
    services_image_url_input: 'Services image',
    services_image_info_message: 'For best resolution, the image should be JPG/JPEG.',
    picture_info_message: 'For best resolution, the image should be JPG/JPEG.',
    special_purpose_input: 'Purpose of the entity',
    special_purpose_placeholder_input: 'Select a purpose',
    social_responses_input: 'Care services',
    social_responses_placeholder_input: "Select your institution's care services",
    save_button: 'Save',
    saving_button: 'Saving...',
    deleting_button: 'Deleting...',
    delete_button: 'Delete entity',
    delete_dialog_title: 'Delete entity "{{name}}"?',
    delete_dialog_content:
      'By deleting the entity, it will be permanently deleted from the system.',
    delete_dialog_action_yes_button: 'Yes, delete',
    delete_dialog_action_no_button: 'Cancel',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the entity "{{name}}"?',
    router_prompt_create_message:
      'By exiting, the entity will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the profile of the entity "{{name}}"?',
    router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the entity profile.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the entity "{{name}}" was canceled.',
    create_cancel_toast: 'The creation of the new entity was canceled.',
    edit_special_purpose_not_allowed_tooltip_title:
      'You do not have permissions to indicate the purpose of the entity.',
    edit_is_active_not_allowed_tooltip_title:
      'You do not have permissions to indicate if the entity is active.',
    not_specified: 'Not specified',
    input_disabled_tooltip: 'You do not have permissions to edit the information of the entity',
    alert: {
      short_name_input: 'This is the name that will be displayed in the system.',
    },
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_success_message: 'The entity "{{name}}" was created with success!',
      edit_success_message: "The changes on the entity's profile were saved.",
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_button: 'Error while deleting',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the entity',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the entity',
      form_validation_invalid_full_name_maximum_characters:
        'The full name must have less than {{max}} characters',
      form_validation_invalid_landline_phone_number: 'The phone number provided is not valid',
      form_validation_invalid_mobile_phone_number: 'The mobile phone number provided is not valid',
      form_validation_not_mobile_phone_number:
        'The number provided does not match a mobile phone number',
      form_validation_invalid_services_phone_number:
        'The services phone number provided is not valid',
      form_validation_invalid_email: 'The email address provided is not valid',
      form_validation_invalid_website: 'The website provided is not valid',
      form_validation_invalid_capacity: 'The capacity provided is not valid',
      form_validation_required_social_responses: "Please indicate the institution's services",
    },
  },
  professional_caregiver_invite_form: {
    exit_button: 'Exit',
    title: 'Invite professional',
    entity_input: 'Entity',
    name_input: 'Name',
    contact_method_input: 'Contact method',
    email_input: 'Email address',
    mobile_phone_number_input: 'Mobile phone number',
    affiliation_type_input: 'Type of affiliation',
    save_dialog_title: 'Send invitation to {{name}}?',
    save_dialog_message:
      'By sending, {{name}} you will receive an invitation to be a professional and have access to some information of the entity {{entity}}.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Send',
    router_prompt_title: 'Are you sure you want exit without sending the invitation?',
    router_prompt_message: 'By exiting, you will lose all information entered in the invitation.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    cancel_toast: 'The invitation was cancelled.',
    save_button: 'Send invitation',
    saving_button: 'Sending...',
    success: {
      save_success_button: 'Sent!',
      success_message: 'The invitation was sent to {{name}}.',
    },
    errors: {
      error_on_save_button: 'Error while sending',
      form_validation_invalid_field: 'Fill in this field to complete the invitation',
      form_validation_invalid_mobile_phone_number: 'The mobile phone number provided is not valid',
      form_validation_not_mobile_phone_number:
        'The number provided does not match a mobile phone number',
      form_validation_invalid_email: 'The email address provided is not valid',
    },
  },
  invite_contact_methods: {
    [InviteContactMethod.Email]: 'Email address',
    [InviteContactMethod.MobilePhoneNumber]: 'Mobile phone number',
  },
  search_select_input: {
    no_options: 'No options',
    loading: 'Loading...',
    more_button: 'More',
    items_showing_information: 'Showing {{value}} of {{total}} items',
  },
  user_card_form: {
    exit_button: 'Exit',
    create_user_card_title: 'Create card',
    edit_user_card_title: 'Edit card',
    view_user_card_title: 'Card details',
    name_input: 'Name',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    tag_input: 'Identification number',
    entity_input: 'Entity',
    user_input: 'User',
    user_info_message: 'This field can only be filled in if an entity is previously selected.',
    save_button: 'Save',
    saving_button: 'Saving...',
    deleting_button: 'Deleting...',
    delete_button: 'Delete card',
    delete_dialog_title: 'Are you sure you want delete card "{{name}}"?',
    delete_dialog_content: 'By deleting the card, it will be permanently deleted from the system.',
    create_dialog_title: 'Save creation of the card "{{name}}"?',
    create_dialog_message: 'By saving, the new card will be added to the system.',
    edit_dialog_title: 'Save changes to the card "{{name}}"?',
    edit_dialog_message: 'By saving, the card will be updated with the newly entered information.',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the card "{{name}}"?',
    router_prompt_create_message:
      'By exiting, the card will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the card "{{name}}"?',
    router_prompt_edit_message: 'By exiting, you will lose all newly entered changes to the card.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the card "{{name}}" was canceled.',
    create_cancel_toast: 'The creation of the new card was canceled.',
    disabled_tooltip: 'You do not have permissions to edit the card',
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_success_message: 'The card "{{name}}" was created with success!',
      edit_success_message: 'The changes on the card were saved.',
      senior_inactive_success: 'The senior {{name}} was marked as inactive.',
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_button: 'Error while deleting',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the card',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the card',
      error_on_delete_user: 'Error while deleting the user',
      error_on_marking_senior_as_inactive: 'Error while marking the senior as inactive',
    },
  },
  device_form: {
    exit_button: 'Exit',
    save_button: 'Save',
    saving_button: 'Saving...',
    create_device_title: 'Create device',
    type_input: 'Type',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    number_input: 'Number',
    entity_input: 'Entity',
    user_input: 'User',
    language_input: 'Language',
    language_placeholder_input: 'Select a language',
    sign_in_type_input: 'Type of sign in',
    sign_in_type_placeholder_input: 'Select a type',
    mac_address_input: 'MAC address',
    serial_number_input: 'Serial number',
    user_info_message: 'This field can only be filled in if an entity is previously selected.',
    edit_type_not_allowed_tooltip_title:
      'You do not have permissions to indicate the type of device.',
    edit_serial_number_not_allowed_tooltip_title:
      'You do not have permissions to indicate the serial number.',
    edit_mac_address_not_allowed_tooltip_title:
      'You do not have permissions to indicate the MAC address.',
    edit_number_not_allowed_tooltip_title: 'You do not have permissions to indicate the number.',
    edit_sign_in_type_not_allowed_tooltip_title:
      'You do not have permissions to indicate the type of sign in.',
    input_disabled_tooltip: 'You do not have permissions to edit the device details',
    deleting_button: 'Deleting...',
    delete_button: 'Delete device',
    delete_dialog_title: 'Are you sure you want delete device with identifier "{{name}}"?',
    delete_dialog_content:
      'By deleting the device, it will be permanently deleted from the system.',
    create_dialog_title: 'Save creation of the device with identifier "{{name}}"?',
    create_dialog_message: 'By saving, the new device will be added to the system.',
    edit_dialog_title: 'Save changes to the device with identifier "{{name}}"?',
    edit_dialog_message:
      'By saving, the device will be updated with the newly entered information.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Save',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the device with identifier "{{name}}"?',
    router_prompt_create_message:
      'By exiting, the device will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the device with identifier "{{name}}"?',
    router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the device.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the device with identifier "{{name}}" was canceled.',
    create_cancel_toast: 'The creation of the new device was canceled.',
    success: {
      create_message: 'The device "{{name}}" was created successfully!',
      edit_message: 'The changes on the device "{{name}}" were saved successfully.',
      deleted_message: 'The device "{{name}}" was deleted.',
    },
    errors: {
      create_message: 'It was not possible to create the device "{{name}}".',
      edit_message: 'It was not possible to save the changes on the device "{{name}}".',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the device',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the device',
      deleted_message: 'It was not possible to delete the device "{{name}}".',
    },
    isInDebugMode_input: 'Debug mode',
    isInDebugMode_placeholder_input: 'To be defined',
  },
  user_sign_in_types: {
    [UserSignInType.Card]: 'Card',
    [UserSignInType.Quick]: 'Quick',
    [UserSignInType.Code]: 'Code',
  },
  entity_special_purposes: {
    [EntitySpecialPurpose.Commercial]: 'Commercial',
    [EntitySpecialPurpose.Demonstration]: 'Demonstration',
    [EntitySpecialPurpose.Research]: 'Research',
    [EntitySpecialPurpose.Testing]: 'Testing',
  },
  entity_statistics_dashboard: {
    title: 'Statistics',
    refresh_button: 'Refresh',
    text_charts: {
      number_of_active_seniors_title: 'Seniors',
      number_of_active_seniors_subtitle: 'Active seniors',
      number_of_active_professional_caregivers_title: 'Professional caregivers',
      number_of_active_professional_caregivers_subtitle:
        'Active caregivers affiliated with the entity',
      number_of_active_informal_caregivers_title: 'Informal caregivers',
      number_of_active_informal_caregivers_subtitle:
        'Active caregivers with relationships with seniors',
      number_of_pending_invites_title: 'Pending invitations',
      number_of_pending_invites_subtitle: 'Invitations awaiting response',
      number_of_calls_title: 'Calls',
      number_of_calls_subtitle: 'Duration greater than or equal to 5 seconds',
      number_of_outgoing_calls_title: 'Outgoing calls',
      number_of_outgoing_calls_subtitle: 'Duration greater than or equal to 5 seconds',
      number_of_incoming_calls_title: 'Incoming calls',
      number_of_incoming_calls_subtitle: 'Duration greater than or equal to 5 seconds',
    },
    table_charts: {
      number_of_active_devices_title: 'Number of active devices',
      number_of_active_digital_inclusion_devices_title: 'Number of digital inclusion devices',
      number_of_active_quick_access_devices_title: 'Number of quick access devices',
      number_of_active_devices_device_type_column: 'Device',
      number_of_active_devices_total_column: 'Total',
      top_users_title: 'Top users',
      top_users_subtitle: 'Hours in activities (duration greater than or equal to 5 seconds)',
      top_users_user_column: 'User',
      top_users_total_usage_time_column: 'Usage time',
      top_users_usage_time_column_unit_label: 'hours',
    },
    area_charts: {
      general_system_usage_title: 'General system usage',
      general_system_usage_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      general_system_usage_x_label: 'Date',
      general_system_usage_y_label: 'Duration',
      general_system_usage_y_unit_label: 'hours',
    },
    bar_charts: {
      calls_evolution_title: 'Calls evolution',
      calls_evolution_subtitle: 'Duration greater than or equal to 5 seconds',
      calls_evolution_x_label: 'Date',
      calls_evolution_y_label: 'Calls',
    },
    pie_charts: {
      most_used_applications_title: 'Most used applications',
      most_used_applications_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      most_used_applications_x_label: 'Application',
      most_used_applications_y_label: 'Duration',
      most_used_applications_y_unit_label: 'hours',
    },
    stack_charts: {
      applications_usage_title: 'Usage by application',
      applications_usage_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      applications_usage_singular_stack_label: 'Application',
      applications_usage_plural_stack_label: 'Applications',
      applications_usage_x_label: 'Date',
      applications_usage_y_label: 'Duration',
      applications_usage_y_unit_label: 'hours',
    },
  },
  senior_statistics_dashboard: {
    title: 'Statistics',
    refresh_button: 'Refresh',
    statistics_per_application_title: 'Statistics by application',
    statistics_per_application_application_label: 'Application',
    statistics_per_application_entity_label: 'Entity',
    statistics_per_application_entity_placeholder: 'Select an entity',
    statistics_per_application_info_message:
      'Select an entity in order to select an application and consult its statistics.',
    text_charts: {
      number_of_informal_caregivers_title: 'Informal caregivers',
      number_of_informal_caregivers_subtitle: 'Relationships with senior',
      number_of_pending_invites_title: 'Pending invitations',
      number_of_pending_invites_subtitle: 'Invitations awaiting response',
      number_of_calls_title: 'Calls',
      number_of_calls_subtitle: 'Duration greater than or equal to 5 seconds',
      number_of_outgoing_calls_title: 'Outgoing calls',
      number_of_outgoing_calls_subtitle: 'Duration greater than or equal to 5 seconds',
      number_of_incoming_calls_title: 'Incoming calls',
      number_of_incoming_calls_subtitle: 'Duration greater than or equal to 5 seconds',
      average_daily_usage_last_title: 'Average daily usage',
      average_daily_usage_subtitle: 'Average of the last 30 days',
      average_daily_usage_unit_label: 'hours',
      number_of_media_received_title: 'Received multimedia',
      number_of_media_received_subtitle: 'Photos and videos',
      number_of_video_messages_received_title: 'Received video messages',
      number_of_video_messages_sent_title: 'Sent video messages',
      number_of_photos_sent_title: 'Sent photos',
      number_of_drawings_sent_title: 'Sent drawings',
      number_of_paintings_sent_title: 'Sent paintings',
      last_access_date_time_title: 'Last access',
    },
    table_charts: {
      top_preferences_title: 'Top preferences',
      top_preferences_subtitle: 'Hours in activities (duration greater than or equal to 5 seconds)',
      top_preferences_application_column: 'Application',
      top_preferences_total_usage_time_column: 'Usage time',
      top_preferences_total_usage_time_column_unit_label: 'hours',
    },
    area_charts: {
      general_system_usage_title: 'General system usage',
      general_system_usage_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      general_system_usage_x_label: 'Date',
      general_system_usage_y_label: 'Duration',
      general_system_usage_y_unit_label: 'hours',
      application_usage_title: '"{{name}}" usage',
      application_usage_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      application_usage_x_label: 'Date',
      application_usage_y_label: 'Duration',
      application_usage_y_unit_label: 'hours',
    },
    pie_charts: {
      most_used_applications_title: 'Most used applications',
      most_used_applications_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      most_used_applications_x_label: 'Application',
      most_used_applications_y_label: 'Duration',
      most_used_applications_y_unit_label: 'hours',
    },
    stack_charts: {
      applications_usage_title: 'Usage by application',
      applications_usage_subtitle:
        'Hours in activities (duration greater than or equal to 5 seconds)',
      applications_usage_singular_stack_label: 'Application',
      applications_usage_plural_stack_label: 'Applications',
      applications_usage_x_label: 'Date',
      applications_usage_y_label: 'Duration',
      applications_usage_y_unit_label: 'hours',
    },
    line_charts: {
      application_evolution_title: '"{{name}}" evolution',
      application_evolution_subtitle: 'Level changes',
      application_evolution_x_label: 'Date',
      application_evolution_y_label: 'Levels',
    },
  },
  charts: {
    no_data: 'There are no data.',
  },
  media_gallery: {
    refresh: 'Refresh',
    no_more_data: 'No more data',
    without_data: 'There are no data',
  },
  application_category_form: {
    exit_button: 'Exit',
    create_application_category_title: 'Create category',
    save_button: 'Save',
    saving_button: 'Saving...',
    default_name_input: 'Name',
    default_description_input: 'Description',
    enabled_state_color_input: 'Enabled state color',
    pressed_state_color_input: 'Pressed state color',
    name_translations_input: 'Translations for the name',
    description_translations_input: 'Translations for the description',
    deleting_button: 'Deleting...',
    delete_button: 'Delete category',
    delete_dialog_title: 'Delete category "{{name}}"?',
    delete_dialog_content:
      'By deleting the category, it will be permanently deleted from the system.',
    delete_dialog_action_yes_button: 'Yes, delete',
    delete_dialog_action_no_button: 'Cancel',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the category "{{name}}"?',
    router_prompt_create_message:
      'By exiting, the category will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the category "{{name}}"?',
    router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the category.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the category "{{name}}" was canceled.',
    create_cancel_toast: 'The creation of the new category was canceled.',
    create_dialog_title: 'Save creation of the category "{{name}}"?',
    create_dialog_message: 'By saving, the new category will be added to the system.',
    edit_dialog_title: 'Save changes to the category "{{name}}"?',
    edit_dialog_message:
      'By saving, the category will be updated with the newly entered information.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Save',
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_success_message: 'The category "{{name}}" was created with success!',
      edit_success_message: 'The changes on the category were saved.',
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_button: 'Error while deleting',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the category',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the category',
      form_validation_invalid_color: 'The indicated color is not valid',
    },
  },
  playlist_types: {
    [PlaylistType.Karaoke]: 'Karaoke',
    [PlaylistType.Movies]: 'Videos',
    [PlaylistType.Music]: 'Music',
  },
  playlists: {
    system_administrator: {
      root: {
        title: 'Playlists',
        search_input_placeholder: 'Search playlists',
        type_filter: 'Type',
        name_header: 'Name',
        no_playlists: 'There are no playlists.',
        add_button: 'Add playlist',
        deleted_playlist_dialog_title: 'Playlist "{{name}}" deleted',
        deleted_playlist_dialog_message: 'The playlist no longer exists in the system.',
        deleted_playlist_dialog_ok_button: 'Ok',
      },
      edit: {
        tabs: {details: 'Details', items: 'Items', entities: 'Entities'},
        items: {
          search_input_placeholder: 'Search items',
          title_header: 'Title',
          no_items: 'There are no items in the playlist.',
          add_button: 'Add item',
          deleted_playlist_item_dialog_title: 'Item "{{name}}" deleted from the playlist',
          deleted_playlist_item_dialog_message: 'The item no longer exists in the system.',
          deleted_playlist_item_dialog_ok_button: 'Ok',
        },
        entities: {
          search_input_placeholder: 'Search entities',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_entities: 'There are no entities.',
          enable_playlist_dialog_title: 'Enable the playlist "{{name}}"?',
          enable_playlist_dialog_message:
            'By enabling the playlist, it will be available to the entity {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Yes, enable',
          enable_playlist_dialog_action_no_button: 'Cancel',
          disable_playlist_dialog_title: 'Disable the playlist "{{name}}"?',
          disable_playlist_dialog_message:
            'By disabling the playlist, it will no longer be available to the entity {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Yes, disable',
          disable_playlist_dialog_action_no_button: 'Cancel',
          errors: {
            enable_playlist_error_message:
              'An error occurred. It was not possible to enable the playlist.',
            disable_playlist_error_message:
              'An error occurred. It was not possible to disable the playlist.',
            update_state_playlist_error_message:
              'An error occurred. It was not possible to update the state of the playlist.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'It was not possible to get the playlist you tried to access.',
        },
      },
      playlist_item: {
        tabs: {details: 'Details', entities: 'Entities'},
        entities: {
          search_input_placeholder: 'Search entities',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_entities: 'There are no entities.',
          enable_item_dialog_title: 'Enable the item "{{name}}" in the playlist?',
          enable_item_dialog_message:
            'By enabling the item in the playlist, it will be available to the entity {{name}}.',
          enable_item_dialog_action_yes_button: 'Yes, enable',
          enable_item_dialog_action_no_button: 'Cancel',
          disable_item_dialog_title: 'Disable the item "{{name}}" in the playlist?',
          disable_item_dialog_message:
            'By disabling the item in the playlist, it will no longer be available to the entity {{name}}.',
          disable_item_dialog_action_yes_button: 'Yes, disable',
          disable_item_dialog_action_no_button: 'Cancel',
          errors: {
            enable_item_error_message:
              'An error occurred. It was not possible to enable the item in the playlist.',
            disable_item_error_message:
              'An error occurred. It was not possible to disable the item in the playlist.',
            update_state_item_error_message:
              'An error occurred. It was not possible to update the state of the item in the playlist.',
          },
        },
        errors: {
          item_does_not_exist_error: 'It was not possible to get the item you tried to access.',
        },
      },
    },
    professional_caregiver: {
      root: {
        search_input_placeholder: 'Search playlists',
        type_filter: 'Type',
        name_header: 'Name',
        state_header: 'State',
        state_value_true: 'Active',
        state_value_false: 'Inactive',
        no_playlists: 'There are no playlists.',
        enable_playlist_dialog_title: 'Enable the playlist "{{name}}"?',
        enable_playlist_dialog_message:
          'By enabling the playlist, it will be available to all users in the entity {{name}}.',
        enable_playlist_dialog_action_yes_button: 'Yes, enable',
        enable_playlist_dialog_action_no_button: 'Cancel',
        disable_playlist_dialog_title: 'Disable the playlist "{{name}}"?',
        disable_playlist_dialog_message:
          'By disabling the playlist, it will no longer be available to all users in the entity {{name}}.',
        disable_playlist_dialog_action_yes_button: 'Yes, disable',
        disable_playlist_dialog_action_no_button: 'Cancel',
        errors: {
          enable_playlist_error_message:
            'An error occurred. It was not possible to enable the playlist.',
          disable_playlist_error_message:
            'An error occurred. It was not possible to disable the playlist.',
          update_state_playlist_error_message:
            'An error occurred. It was not possible to update the state of the playlist.',
        },
      },
      edit: {
        tabs: {
          items: 'Items',
          users: 'Users',
          details: 'Details',
        },
        items: {
          search_input_placeholder: 'Search items',
          title_header: 'Title',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_items: 'There are no items in the playlist.',
          enable_item_dialog_title: 'Enable the item "{{name}}" in the playlist?',
          enable_item_dialog_message:
            'By enabling the item in the playlist, it will be available to all users in the entity {{name}}.',
          enable_item_dialog_action_yes_button: 'Yes, enable',
          enable_item_dialog_action_no_button: 'Cancel',
          disable_item_dialog_title: 'Disable the item "{{name}}" in the playlist?',
          disable_item_dialog_message:
            'By disabling the item in the playlist, it will no longer be available to all users in the entity {{name}}.',
          disable_item_dialog_action_yes_button: 'Yes, disable',
          disable_item_dialog_action_no_button: 'Cancel',
          errors: {
            enable_item_error_message:
              'An error occurred. It was not possible to enable the item in the playlist.',
            disable_item_error_message:
              'An error occurred. It was not possible to disable the item in the playlist.',
            update_state_item_error_message:
              'An error occurred. It was not possible to update the state of the item in the playlist.',
          },
        },
        users: {
          search_input_placeholder: 'Search users',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_users: 'There are no users.',
          enable_playlist_dialog_title: 'Enable the playlist "{{name}}"?',
          enable_playlist_dialog_message:
            'By enabling the playlist, it will be available to {{name}}.',
          enable_playlist_dialog_action_yes_button: 'Yes, enable',
          enable_playlist_dialog_action_no_button: 'Cancel',
          disable_playlist_dialog_title: 'Disable the playlist "{{name}}"?',
          disable_playlist_dialog_message:
            'By disabling the playlist, it will no longer be available to {{name}}.',
          disable_playlist_dialog_action_yes_button: 'Yes, disable',
          disable_playlist_dialog_action_no_button: 'Cancel',
          errors: {
            enable_playlist_error_message:
              'An error occurred. It was not possible to enable the playlist.',
            disable_playlist_error_message:
              'An error occurred. It was not possible to disable the playlist.',
            update_state_playlist_error_message:
              'An error occurred. It was not possible to update the state of the playlist.',
          },
        },
        errors: {
          playlist_does_not_exist_error:
            'It was not possible to get the playlist you tried to access.',
        },
      },
      playlist_item: {
        tabs: {
          users: 'Users',
          details: 'Details',
        },
        users: {
          search_input_placeholder: 'Search users',
          name_header: 'Name',
          type_header: 'Type',
          state_header: 'State',
          state_value_true: 'Active',
          state_value_false: 'Inactive',
          no_users: 'There are no users.',
          enable_item_dialog_title: 'Enable the item "{{name}}" in the playlist?',
          enable_item_dialog_message:
            'By enabling the item in the playlist, it will be available to {{name}}.',
          enable_item_dialog_action_yes_button: 'Yes, enable',
          enable_item_dialog_action_no_button: 'Cancel',
          disable_item_dialog_title: 'Disable the item "{{name}}" in the playlist?',
          disable_item_dialog_message:
            'By disabling the item in the playlist, it will no longer be available to {{name}}.',
          disable_item_dialog_action_yes_button: 'Yes, disable',
          disable_item_dialog_action_no_button: 'Cancel',
          errors: {
            enable_item_error_message:
              'An error occurred. It was not possible to enable the item in the playlist.',
            disable_item_error_message:
              'An error occurred. It was not possible to disable the item in the playlist.',
            update_state_item_error_message:
              'An error occurred. It was not possible to update the state of the item in the playlist.',
          },
        },
        errors: {
          item_does_not_exist_error: 'It was not possible to get the item you tried to access.',
        },
      },
    },
  },
  user_playlists: {
    search_input_placeholder: 'Search playlists',
    type_filter: 'Type',
    entity_filter: 'Entity',
    name_header: 'Name',
    state_header: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    state_disabled_tooltip: 'You do not have permissions to edit the state of the playlist',
    no_playlists: 'There are no playlists.',
    no_affiliations_info_message:
      "The user has no affiliation with any entity. Therefore, it is not possible to consult the user's playlists.",
    enable_playlist_dialog_title: 'Are your sure you want enable the playlist "{{name}}"?',
    enable_playlist_dialog_message: 'By enabling the playlist, it will be available to {{name}}.',
    enable_playlist_for_entity_dialog_message:
      'By enabling the playlist, it will be available to {{name}} in the entity {{entity}}.',
    enable_playlist_dialog_action_yes_button: 'Yes, enable',
    enable_playlist_dialog_action_no_button: 'Cancel',
    disable_playlist_dialog_title: 'Are your sure you want disable the playlist "{{name}}"?',
    disable_playlist_dialog_message:
      'By disabling the playlist, it will no longer be available to {{name}}.',
    disable_playlist_for_entity_dialog_message:
      'By disabling the playlist, it will no longer be available to {{name}} in the entity {{entity}}.',
    disable_playlist_dialog_action_yes_button: 'Yes, disable',
    disable_playlist_dialog_action_no_button: 'Cancel',
    errors: {
      enable_playlist_error_message:
        'An error occurred. It was not possible to enable the playlist.',
      disable_playlist_error_message:
        'An error occurred. It was not possible to disable the playlist.',
      update_state_playlist_error_message:
        'An error occurred. It was not possible to update the state of the playlist.',
    },
  },
  time_lapse_types: {
    [TimeLapseType.Custom]: 'Custom',
    [TimeLapseType.Last180Days]: 'Last 180 days',
    [TimeLapseType.Last30Days]: 'Last 30 days',
    [TimeLapseType.Last365Days]: 'Last 365 days',
    [TimeLapseType.LastMonth]: 'Last month',
    [TimeLapseType.LastWeek]: 'Last week',
    [TimeLapseType.ThisMonth]: 'This month',
    [TimeLapseType.ThisWeek]: 'This week',
  },
  time_lapse: {
    dialog_title: 'Choose a time range',
    range_label: 'Range type',
    start_text: 'Start',
    end_text: 'End',
    to_text: 'to',
    cancel_button: 'Cancel',
    confirm_button: 'Confirm',
  },
  device_health: {
    no_data: 'There are no data.',
    last_recording: 'Last recording',
    basic_information_section_title: 'Basic information',
    wifi_section_title: 'Wi-Fi',
    networks_wifi_subsection_title: 'Available networks',
    current_wifi_subsection_title: 'Connected networks',
    mobile_data_section_title: 'Mobile data',
    graphics_section_title: 'Graphics',
    controllers_graphics_subsection_title: 'Controllers',
    displays_graphics_subsection_title: 'Displays',
    system_section_title: 'System',
    battery_section_title: 'Battery',
    disks_section_title: 'Disks',
    operating_system_section_title: 'Operating system',
    cpu_section_title: 'CPU',
    memory_section_title: 'Memory',
    applications_section_title: 'Applications',
    versions_section_title: 'Versions',
    request_tracking_section_title: 'Tracking',
    cache_images_section_title: 'Cache (images)',
    creation_date_label: 'Creation date',
    expiration_date_label: 'Expiration date',
    entity_label: 'Entity',
    user_label: 'User',
    data_usage_label: 'Data usage',
    ssid_label: 'SSID',
    quality_label: 'Quality',
    ip_address_label: 'IP address',
    is_data_usage_enabled_label: 'Is data usage enabled?',
    carrier_label: 'Carrier',
    network_type_label: 'Network type',
    dialer_label: 'Dialer',
    signal_strength_label: 'Signal strength',
    signal_strength_level_label: 'Level of signal strength',
    signal_strength_no: 'No signal',
    signal_strength_weak: 'Weak',
    signal_strength_fair: 'Fair',
    signal_strength_good: 'Good',
    signal_strength_strong: 'Strong',
    brand_label: 'Brand',
    model_label: 'Model',
    is_main_display_label: 'Is the main display?',
    width_label: 'Width',
    height_label: 'Height',
    current_width_label: 'Current width',
    current_height_label: 'Current height',
    current_scale_factor_label: 'Scale factor',
    sku_label: 'SKU',
    serial_number_label: 'Serial number',
    version_label: 'Version',
    has_battery_label: 'Has a battery?',
    is_battery_charging_label: 'Is charging?',
    is_battery_connected_to_power_label: 'Is connected to power?',
    battery_level_label: 'Level',
    size_label: 'Size',
    used_disks_label: 'Used',
    available_label: 'Available',
    mount_label: 'Mount',
    name_label: 'Name',
    distribution_label: 'Distribution',
    release_label: 'Release',
    build_label: 'Build',
    web_view_version_label: 'WebView version',
    cores_label: 'Number of cores',
    used_memory_label: 'Used',
    country_label: 'Country',
    region_label: 'Region',
    city_label: 'City',
    city_longitude_label: 'City longitude',
    city_latitude_label: 'City latitude',
    language_label: 'Language',
    user_ip_address_label: 'User IP address',
    yes_label: 'Yes',
    no_label: 'No',
    gb_unit_label: 'GB',
    mb_unit_label: 'MB',
    px_unit_label: 'px',
    agent_label: 'Agent',
    chrome_label: 'Chrome',
    electron_label: 'Electron',
    launcher_binary_label: 'Launcher binary',
    launcher_frontend_label: 'Launcher frontend',
  },
  operative_system_names: {
    [OperativeSystemName.Android]: 'Android',
    [OperativeSystemName.Linux]: 'Linux',
    [OperativeSystemName.MacOS]: 'MacOS',
    [OperativeSystemName.Other]: 'Other',
    [OperativeSystemName.Windows]: 'Windows',
    [OperativeSystemName.iOS]: 'iOS',
  },
  mobile_network_types: {
    [MobileNetworkType.Unspecified]: 'Other',
    [MobileNetworkType._1G]: '1G',
    [MobileNetworkType._2G]: '2G',
    [MobileNetworkType._3G]: '3G',
    [MobileNetworkType._4G]: '4G',
    [MobileNetworkType._5G]: '5G',
  },
  home: {
    refreshing: 'Refreshing...',
    release_to_refresh: 'Release to refresh',
    pull_to_refresh: 'Pull to refresh',
    none: {
      root: {
        no_associations_message_title:
          'Your account to access the {{brand}} platform is not associated with any senior or entity at the moment.',
        no_associations_message_subtitle:
          'Please, request an invitation to other caregivers or contact the Customer Support.',
      },
    },
  },
  localized_string_input: {
    cancel_button: 'Cancel',
    confirm_button: 'Confirm',
    has_zero_translations_tooltip_message:
      'There are no translations defined out of a total of {{total}} possible ones.',
    has_one_translation_tooltip_message:
      'There is one translation defined out of a total of {{total}} possible ones.',
    has_many_translations_tooltip_message:
      'There are {{count}} translations defined out of a total of {{total}} possible ones.',
  },
  user_playlist_items: {
    title: 'Playlist "{{name}}"',
    search_input_placeholder: 'Search items',
    title_header: 'Title',
    state_header: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    state_disabled_tooltip: 'You do not have permissions to edit the state of the item',
    no_items: 'There are no items in the playlist.',
    enable_item_dialog_title: 'Are your sure you want enable the item "{{name}}" in the playlist?',
    enable_item_dialog_message:
      'By enabling the item in the playlist, it will be available to {{name}}.',
    enable_item_dialog_action_yes_button: 'Yes, enable',
    enable_item_dialog_action_no_button: 'Cancel',
    disable_item_dialog_title:
      'Are your sure you want disable the item "{{name}}" in the playlist?',
    disable_item_dialog_message:
      'By disabling the item in the playlist, it will no longer be available to {{name}}.',
    disable_item_dialog_action_yes_button: 'Yes, disable',
    disable_item_dialog_action_no_button: 'Cancel',
    errors: {
      enable_item_error_message:
        'An error occurred. It was not possible to enable the item in the playlist.',
      disable_item_error_message:
        'An error occurred. It was not possible to disable the item in the playlist.',
      update_state_item_error_message:
        'An error occurred. It was not possible to update the state of the item in the playlist.',
      playlist_does_not_exist_error: 'It was not possible to get the playlist you tried to access.',
    },
  },
  playlist_form: {
    exit_button: 'Exit',
    create_playlist_title: 'Create playlist',
    save_button: 'Save',
    saving_button: 'Saving...',
    default_name_input: 'Name',
    name_translations_input: 'Translations for the name',
    default_description_input: 'Description',
    description_translations_input: 'Translations for the description',
    enabled_state_color_input: 'Enabled state color',
    pressed_state_color_input: 'Pressed state color',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    type_input: 'Type',
    active_by_default_for_all_entities_input: 'Active by default for all entities?',
    active_by_default_for_all_entities_placeholder_input: 'Select an option',
    yes_value: 'Yes',
    no_value: 'No',
    image_url_input: 'Image',
    image_info_message:
      'The image must be of type JPG/JPEG. For better results, use the resolution of 300x300px.',
    regional_lockout_input: 'Regional lockout',
    regional_lockout_placeholder: 'Select regions',
    deleting_button: 'Deleting...',
    delete_button: 'Delete playlist',
    delete_dialog_title: 'Are you sure you want delete playlist "{{name}}"?',
    delete_dialog_content:
      'By deleting the playlist, it will be permanently deleted from the system.',
    delete_dialog_action_yes_button: 'Yes, delete',
    delete_dialog_action_no_button: 'Cancel',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Save',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the playlist "{{name}}"?',
    router_prompt_create_message:
      'By exiting, the playlist will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the playlist "{{name}}"?',
    router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the playlist.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the playlist "{{name}}" was canceled.',
    create_cancel_toast: 'The creation of the new playlist was canceled.',
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_success_message: 'The playlist "{{name}}" was created with success!',
      edit_success_message: 'The changes on the playlist were saved.',
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_button: 'Error while deleting',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the playlist',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the playlist',
      form_validation_invalid_color: 'The indicated color is not valid',
    },
  },
  playlist_item_form: {
    exit_button: 'Exit',
    create_playlist_item_title: 'Create item in playlist',
    save_button: 'Save',
    saving_button: 'Saving...',
    deleting_button: 'Deleting...',
    delete_button: 'Delete item in playlist',
    delete_dialog_title: 'Are you sure you want delete item "{{name}}" in the playlist?',
    delete_dialog_content:
      'By deleting the item in the playlist, it will be permanently deleted from the system.',
    delete_dialog_action_yes_button: 'Yes, delete',
    delete_dialog_action_no_button: 'Cancel',
    create_dialog_title: 'Save creation of the item "{{name}}" in the playlist?',
    create_dialog_message: 'By saving, the new item for the playlist will be added to the system.',
    save_dialog_action_cancel_button: 'Cancel',
    save_dialog_action_save_button: 'Save',
    router_prompt_create_title:
      'Are you sure you want exit without saving the creation of the item "{{name}}" in the playlist?',
    router_prompt_create_message:
      'By exiting, the item in the playlist will not be saved and all information entered in it will be lost.',
    router_prompt_edit_title:
      'Are you sure you want exit without saving changes to the item "{{name}}" in the playlist?',
    router_prompt_edit_message:
      'By exiting, you will lose all newly entered changes to the item in the playlist.',
    router_prompt_action_cancel_button: 'Cancel',
    router_prompt_action_ok_button: 'Yes, exit',
    edit_cancel_toast: 'The edition of the item "{{name}}" in the playlist was canceled.',
    create_cancel_toast: 'The creation of the new item in the playlist was canceled.',
    default_title_input: 'Title',
    title_translations_input: 'Translations for the title',
    default_description_input: 'Description',
    description_translations_input: 'Translations for the description',
    regional_lockout_input: 'Regional lockout',
    regional_lockout_placeholder: 'Select regions',
    state_input: 'State',
    state_value_true: 'Active',
    state_value_false: 'Inactive',
    playlist_input: 'Playlist',
    resource_url_input: 'Resource URL',
    resource_input: 'Resource',
    thumbnail_image_url_input: 'Image',
    thumbnail_image_info_message:
      'The image must be of type JPG/JPEG. For better results, use the resolution of 300x300px.',
    success: {
      save_success_button: 'Saved!',
      delete_success_button: 'Deleted!',
      create_success_message: 'The item "{{name}}" was created with success in the playlist!',
      edit_success_message: 'The changes on the item in the playlist were saved.',
    },
    errors: {
      error_on_save_button: 'Error while saving',
      error_on_delete_button: 'Error while deleting',
      edit_form_validation_invalid_field: 'Fill in this field to complete editing the item',
      create_form_validation_invalid_field:
        'Fill in this field to complete the creation of the item',
      form_validation_invalid_resource_url: 'The resource URL is not valid',
    },
  },
  licenses: {
    professional_caregiver: {
      root: {
        refresh_button: 'Refresh',
        number_of_monthly_active_licenses_title: 'Monthly active licenses',
        number_of_active_seniors_title: 'Active seniors',
        number_of_monthly_active_licenses_not_defined: 'Not defined',
      },
    },
  },
  videocall: {
    you: 'You',
    incomingCall: 'Is calling you...',
    incomingCallConnecting: 'Connecting..',
    incomingCallConnectingHint: '(this may take a little while)',
    answer: 'Answer',
    decline: 'Decline',
    settingsTitle: 'Settings',
    settingsMicrophone: 'Microphone',
    settingsCamera: 'Camera',
    settingsSpeaker: 'Speaker',
    default: 'Default',
    callFailed: {
      title: 'Call failed',
      ok: 'OK',
      e486: 'Destination number is busy',
      e487: 'Request terminated',
      e603: 'Call was rejected',
      e404: 'Invalid number',
      e480: 'Destination number is unavailable',
      e402: 'Insufficient funds',
    },
  },
  entityFrontCardDesignTool: {
    title: 'Entity front card design',
    step1: 'Entity',
    step2: 'Users',
    step3: 'Results',
    entityPicture: 'Entity picture',
    chooseAnotherEntityPicture: 'Choose another entity picture',
    entityName: 'Entity name',
    addEntityName: 'Add entity name',
    entityNameToBePrinted: 'Entity name to be printed',
    entityNameTextColor: 'Entity name text color',
    entityNameTextFont: 'Entity name text font',
    next: 'Next',
    save: 'Save',
    restart: 'Restart',
    failedToLoadUsers: 'Failed to load users',
  },
  entityBackCardDesignTool: {
    title: 'Entity back card design',
    step1: 'Specs',
    step2: 'Preview',
    entityName: 'Entity name',
    entityNumber: 'Entity number',
    seniorUsersCount: 'Senior users count',
    caregiverUsersCount: 'Caregiver users count',
    cardName: 'Card name',
    entityPictureHeight: 'Entity picture height',
    lineColor: 'Line color',
    includeEntityName: 'Include entity name',
    generatePreview: 'Generate preview',
    saveCards: 'Save cards',
    saveSpreadsheet: 'Save spreadsheet',
    restart: 'Restart',
  },
  cards: {
    pendingInvitation: 'Pending invitation',
    years: 'years',
  },
  dashboard_side_menu: {
    dashboard: 'Dashboard',
    general_activity: 'General activity',
    statistics: 'Statistics',
    general_statistics: 'General statistics',
    social_statistics: 'Social statistics',
  },
  digital_inclusion_side_menu: {
    applications: 'Applications',
    details: 'Details',
    users: 'Users',
    photos: 'Photos',
    cards: 'Cards',
    devices: 'Devices',
    services: 'Services',
    playlists: 'Playlists',
  },
  seniorSideMenu: {
    generalStatistics: 'General Statistics',
    healthStatistics: 'Health Statistics',
    basicActivitiesDailyLife: 'DLBA',
    careAndActivity: 'Care summary',
    individualPlan: 'Individual Plan',
    personalData: 'Personal data',
    careServices: 'Care Service',
  },
  teamSideMenu: {
    professionals: 'Professionals',
    shifts: {
      title: 'Shifts',
      shiftsManagement: 'Shift Management',
      shiftsAllocations: 'Shift allocations',
    },
    attendanceTracker: {
      title: 'Attendance Tracker',
      assignments: 'Assignments',
      registry: 'Records',
    },
    careServices: 'General Service Schedule',
    permissions: 'Permissions',
    profileDropdown: {
      profile: 'Profile',
      careServices: 'Care Service',
      applications: 'Applications',
      cards: 'Cards',
      playLists: 'Playlists',
    },
  },
  profileSideMenu: {
    profile: 'Profile',
  },
  settingsSideMenu: {
    institution: 'Institution',
    activities_sectors: {
      load_error: 'Could not load activities sectors',
    },
  },
  responsibleUserDropdown: {
    noResponsibleDefined: 'No responsible defined',
    noCoordinatorDefined: 'No coordinator defined',
  },
  activities_group_entry: {
    modals: {
      edit: {
        title: 'Edit details',
        observations_max_characters: 'Max. 120 characters',
      },
      confirm_delete: {
        title: 'Delete ABVD - {{ name }}',
        copy: {
          title: 'Are you sure you want to delete the activity?',
          subtitle: 'This action will delete the activity from the list.',
        },
      },
    },
    activity_types: 'Tasks',
    no_activity_types: 'No tasks added yet',
    configuration_type_not_defined: 'Not defined',
    configuration_type_label: 'Type',
    exceptions: 'Exceptions',
    edit_details: 'Edit details',
    add_activities: 'Add',
    configure_first: 'Please configure the activity group repetition type first.',
    actions: {
      exit: 'Exit',
      edit: 'Edit',
      delete: 'Delete',
      history: 'History',
    },
    snackbar: {
      create: {
        success: 'The activity was added successfully!',
        error: 'Could not add the activity.',
      },
      update: {
        success: 'The activity was updated successfully!',
        error: 'Could not update the activity.',
      },
      delete: {
        success: 'The activity was deleted successfully!',
        error: 'Could not delete the activity.',
      },
      configuration: {
        success: 'The activity was loaded with success!',
        error: 'Could not load the activity.',
      },
    },
    error: 'Could not load Activities Group Entry Configuration data.',
  },
  activities_type_entry: {
    modals: {
      edit: {
        title: 'Task details',
      },
      delete: {
        title: 'Details - {{ type }}',
        copy: {
          title: 'Are you sure you want to remove {{ type }} from the activities?',
          subtitle: "The activity will be removed from the senior's activity list.",
        },
      },
      view: {
        leave: 'Leave',
        view_only: 'Only for visualization, cannot be edited.',
      },
    },
    snackbar: {
      update: {
        success: 'The task was updated successfully!',
        error: 'Could not update the task.',
      },
      delete: {
        success: 'The task was deleted successfully!',
        error: 'Could not delete the task.',
      },
      configuration: {
        success: 'The task was loaded with successfully!',
        error: 'Could not load the task.',
      },
    },
    templates: {
      name: 'Template name',
      description: 'Description',
      activities_save: 'Activities to save',
      activities_import: 'Select activities',
      check_all: 'Check all',
      snackbar: {
        success_message:
          'The "{{name}}" template was saved successfully. You can edit or delete models in the settings.',
        error_message: 'The "{{name}}" template could not be saved.',
        import_success_message: 'The "{{name}}" template was applied correctly.',
        import_error_message: 'The "{{name}}" template could not be applied.',
      },
      template: 'Template',
      confirmation_title: 'The selected "{{name}}" template will be used for this user',
      confirmation_subtitle: 'You can edit or delete selected activities any time',
      confirmation_replace_title:
        'Importing the "{{name}}" template, the current plan data will be replaced',
      confirmation_replace_subtitle: 'To proceed, write the word "CONFIRM"',
      confirm: 'confirm',
      name_error: 'The template name must have less than {{max}} characters',
      description_error: 'The description must have less than {{max}} characters',
      select: 'Select one template',
    },
  },
  dynamic_form: {
    required_message: `Required field.`,
    pattern_message: {
      invalid: `Invalid field.`,
      max_characters: 'Maximum {{ max }} characters',
    },
    error_loading: 'An error occurred while loading the form.',
    feature_fields: {
      autocomplete: {
        load_more: 'Load more',
        loading: 'Loading more...',
        no_options: 'No options',
        placeholder: 'Select an item',
        counter: 'Showing {{ current }} out of {{ max }} items',
      },
    },
  },
  modal: {
    ok: 'Ok',
    cancel: 'Cancel',
    confirm: 'Confirm',
    add: 'Add',
    save: 'Save',
    remove: 'Remove',
    import: 'Import',
    back: 'Back',
    next: 'Next',
    delete: 'Delete',
    revert: 'Revert',
    archive: 'Archive',
  },
  daily_care_log: {
    generics: {
      back: 'Back',
    },
    senior_actions: {
      open_actions: 'Open actions for {{name}}',
      title: 'Actions for {{name}}',
    },
    tabs: {
      home: 'Home',
      occurrences: 'Occurrences',
      attendance_tracker: 'Time clock',
    },
    date_carousel: {
      yesterday: 'Yesterday',
      today: 'Today',
      tomorrow: 'Tomorrow',
    },
    task_table: {
      tasks: 'Tasks',
      empty: 'No registered tasks.',
      select_all: 'Select all users',
      sort: 'Order',
      drawer: {
        clear: 'Clear',
        save: 'Save',
        filters: {
          title: 'Filters',
          social_responses: 'Care services',
        },
        sort: {
          title: 'Sort by',
          name: 'Name',
          autonomy: 'Autonomy',
          status: 'Status',
          ascending: 'Ascending',
          descending: 'Descending',
        },
      },
    },
    cards: {
      entity_user: {
        task_conclusion_summary_label: {
          concluded: 'Concluded',
          task: 'Task',
        },
      },
    },
    home: {
      my_shifts: 'My shifts',
      care_services: 'Care services',
      other_shifts: 'Other shifts',
      no_allocated_shifts: 'No shifts allocated for this day',
      no_other_shifts: 'No other shifts for this day',
      no_care_services: 'No services allocated for this day',
      previous_days_shift: "Previous day's shift",
    },
    seniors: {
      title: 'Seniors',
      no_seniors: 'No seniors registered yet.',
    },
    task_details: {
      task_not_done_reason: 'Task not done reason',
      task_done: 'Realized',
      task_not_done: 'Not realized',
      add_a_note: 'Write an observation...',
      cancel: 'Cancel',
      save: 'Save',
      register: 'Register',
      edit: 'Edit',
      edit_entry: 'Edit record',
      disabled_tooltip: 'You do not have permissions to edit this task.',
    },
    shift_collapsible: {
      shift: 'Shift',
      other_shift_members: 'Other shift members',
      no_other_shift_members: 'No other members in the shift',
    },
    shift_details: {
      all_tasks_handled: 'All the tasks for the shift have been completed!',
      tabs: {
        simple: {title: 'Simple'},
        grouped: {title: 'Grouped', task: 'Task'},
        user: {
          title: 'User',
          grid: {
            empty: 'No users',
            empty_search_message: {
              title: 'Sorry, we could not find any results for "{{ name }}".',
              subtitle: 'Try a different search.',
            },
          },
        },
      },
      unplanned_tasks: {
        header_title: 'New unplanned task',
        activity_sector: 'Activity sector',
        activity: 'Activity',
        name: 'Task name',
        execution_date: 'Execution date',
        execution_hour: 'Execution hour',
        add_success_toast: 'The task was added successfully!',
        add_error_toast: 'It was not possible to add the task.',
        upload_error_toast: 'It was not possible to upload the files.',
        add_multiple_success_toast: 'The tasks were added successfully!',
        add_cancel_modal_title: 'Are you sure you want to exit without adding the task?',
        add_cancel_modal_subtitle: 'The data will not be saved.',
        add_user_placeholder: 'Add a user',
        add_files: 'Add files',
        delete_attachment: 'Are you sure you want to delete the file "{{ name }}"?',
        files_size: 'Maximum size per fils is {{ size }}MB',
        file_size_error: 'Size exceeds the maximum of {{ size }}MB',
        file_not_supported_error: 'File type not supported',
        file_both_errors: 'Size exceeds the maximum of {{ size }}MB and file type not supported',
      },
    },
    grouped_tasks_details: {
      select_bottom_bar: {
        count: '{{ count }} selected user',
        count_plural: '{{ count }} selected users',
        next: 'Next',
      },
    },
    grouped_tasks_handling: {
      snackbar: {
        loading_tasks: {
          error: 'Error on loading tasks!',
        },
        updating_tasks: {
          success: 'The tasks were registered successfully!',
          error: 'Error when registering tasks.',
        },
      },
    },
    attendance_tracker: {
      title: 'Attendance Tracker',
      empty_title: 'There are no attendance records yet',
      empty_subtitle: 'Press the button to clock in',
      register: 'Clock in',
      filters: {
        sections: {
          attendanceMovements: 'Movement',
          attendanceStatus: 'Status',
          attendancePeriods: 'Period of the day',
        },
        values: {
          voided: 'Voided',
          entry: 'Entries',
          exit: 'Exits',
          open: 'In progress',
          closed: 'Completed',
          morning: 'Morning',
          afternoon: 'Afternoon',
          night: 'Night',
        },
      },
      session: {
        entry: 'Entry',
        exit: 'Exit',
        running: 'Happening now',
        overdue: 'To be closed',
        closed: 'Completed',
        elapsed: '{{duration}} elapsed',
        overdue_alert: 'You have an open entry since {{date}}. The exit has to be registered.',
        total: 'Total: {{duration}}',
      },
      bar: {
        running: 'In progress',
        since: 'Since {{date}}',
        not_started: 'Time clock not started',
      },
      manual: {
        registered_by: 'Registered by {{name}}',
        entry_registered_by: 'Entry registered by {{name}}',
        exit_registered_by: 'Exit registered by {{name}}',
        notice: 'Registered manually by {{name}} on {{date}}, on behalf of the professional.',
        reason: 'Reason: {{reason}}',
        no_location: 'No location (manual record)',
        unknown_user: 'another user',
      },
      voided: {
        tag: 'Voided',
        notice:
          'This session was voided by {{name}} on {{date}} and does not count as time worked anymore.',
        reason: 'Reason: {{reason}}',
      },
      register_entry: 'Clock in',
      register_exit: 'Clock out',
      load_error: 'Your attendance records could not be obtained.',
      empty_other_day: 'There are no attendance records on this day.',
      details: {
        no_exit: 'The exit has not been registered yet.',
        open_in_maps: 'Open in Google Maps',
        time_zone: 'Time zone',
      },
      new: {
        title_entry: 'Clock in',
        title_exit: 'Clock out',
        save_entry: 'Register',
        save_exit: 'Register',
        record_type: 'Record type',
        entry: 'Entry',
        exit: 'Exit',
        professional: 'Professional',
        entity: 'Entity',
        date_time: 'Date and time',
        location: 'Location',
        latitude: 'Latitude',
        longitude: 'Longitude',
        accuracy: 'Accuracy',
        accuracy_value: '± {{meters}} m',
        waiting: 'Getting your exact location...',
        accuracy_info:
          'The current accuracy is ± {{current}} m. It is the best the device can get right now and keeps being updated. If you are indoors, move closer to a window or go outside to improve it.',
        unavailable: 'Your location could not be obtained. Check that the GPS of the device is on.',
        loading_error:
          'Your last attendance record could not be obtained. Check the connection and try again.',
        retry: 'Try again',
        success_entry: 'Clock in registered successfully!',
        success_exit: 'Clock out registered successfully!',
        type_conflict:
          'Your last attendance record changed. Check the type of record and register again.',
        save_error: 'The attendance could not be registered. Try again.',
        purpose:
          'Your exact location is collected only at the moment you clock in, to prove where the clock in was made. This data is personal and is linked to your record.',
        permission: {
          prompt_title: 'We need your location',
          prompt_description:
            'To clock in you have to prove where you are. Your location is only requested on this screen and is stored only at the moment you clock in.',
          allow: 'Allow location',
          denied_title: 'Location is blocked',
          denied_description:
            'Location access was denied in this browser, so it is not possible to clock in. To allow it again:',
          unsupported_title: 'Location unavailable',
          unsupported_description:
            'This browser does not support location or the page is not opened over a secure connection (HTTPS).',
          help: {
            chrome:
              'Click the icon on the left of the address (lock or settings), choose "Site settings" and, under "Location", select "Allow". Then reload the page.',
            edge: 'Click the lock on the left of the address, open "Permissions for this site" and, under "Location", select "Allow". Then reload the page.',
            firefox:
              'Click the permissions icon on the left of the address, remove the block on "Access your location" and allow it. Then reload the page.',
            safari:
              'Open "Safari" > "Settings" > "Websites" > "Location", choose this site and select "Allow". Then reload the page.',
            ios: 'Open "Settings" > "Privacy & Security" > "Location Services", turn them on and allow access to the browser you are using. Then reload the page.',
            android_chrome:
              'Tap the icon on the left of the address, open "Permissions" > "Location" and select "Allow". If the block is on the phone, turn location on in "Settings" > "Location". Then reload the page.',
            generic:
              'Open the site permissions settings of your browser, allow access to location and reload the page.',
          },
        },
      },
    },
    occurrences: {
      empty_title: 'No occurrences yet.',
      empty_filters_title: 'No occurrences found to match the selected filters.',
      empty_subtitle: 'Press the button to add a new occurrence',
      new: 'New',
      new_occurrence: 'New occurrence',
      add: 'Add',
      type: 'Occurrence type',
      date: 'Occurrence date',
      time: 'Occurrence time',
      local: 'Occurrence local',
      responsible: 'Occurrence responsible',
      patient: 'Involved patient',
      details: 'Occurrence details',
      add_success_toast: 'The occurrence was added successfully!',
      add_error_toast: 'An error occurred when adding the occurrence.',
      add_cancel_modal_title: 'Are you sure you want to exit without adding the occurrence?',
      add_cancel_modal_subtitle: 'The data will not be saved.',
      local_error: 'The entered local cannot exceed {{ characters }} characters',
    },
  },
  care_and_activities: {
    date: 'Date',
    activity: 'Activity',
    task: 'Task',
    evaluation: 'Evaluation',
    responsible: 'Handled by',
    status: 'Status',
    empty_state: 'No tasks registered.',
    permissions: 'You do not have permission to view the tasks.',
    register: 'Register',
    notes: 'Notes',
    status_types: {
      [CaregiverTaskCompletion.DONE]: 'Realized',
      [CaregiverTaskCompletion.NOT_DONE]: 'Not realized',
      [CaregiverTaskCompletion.PLANNED]: 'Planned',
      [CaregiverTaskCompletion.MISSED]: 'Not treated',
    },
    sort_icon: {
      sort_in_ascending: 'Sort in ascending',
      sort_in_descending: 'Sort in descending',
    },
    handle_task_modal: {
      task_status: {
        done: 'Done',
        not_done: 'Not done',
      },
      details: 'Details',
      observations: 'Observations',
      treated: 'Treated',
      cancel: 'Cancel',
      write_observation: 'Write an observation.',
      observation_required_message: 'Please provide more information on the reason',
      card_collapse: {
        title: 'Task details',
      },
      time_input: 'Time of execution',
      date_input: 'Date registered',
      select_date: 'Select a date',
      select_time: 'Select a time',
      task_info_load_error: 'Error loading task information.',
      feature_input: {
        label: {
          [CareTaskFeature.BloodGlucoseHealthMeasurement]: 'Blood glucose',
          [CareTaskFeature.BloodOxygenHealthMeasurement]: 'Oxygen in the blood',
          [CareTaskFeature.BloodPressureHealthMeasurement]: 'Blood pressure (SIS - DIA/mmHg)',
          [CareTaskFeature.BodyFatPercentageHealthMeasurement]: 'Fat percentage',
          [CareTaskFeature.BodyHeightHealthMeasurement]: 'Height',
          [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: 'Percentage of muscle',
          [CareTaskFeature.BodyTemperatureHealthMeasurement]: 'Body temperature',
          [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: 'Percentage of water',
          [CareTaskFeature.BodyWeightHealthMeasurement]: 'Weight',
          [CareTaskFeature.HeartRateHealthMeasurement]: 'Heart rate',
          [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]: 'Natriuretic peptide test',
          [CareTaskFeature.RespiratoryRateHealthMeasurement]: 'Respiratory rate',
          [CareTaskFeature.SleepHealthMeasurement]: 'Sleep health measurement',
          [CareTaskFeature.StepsHealthMeasurement]: 'Steps',
        },
        inputs: {
          value: {
            label: 'Value',
            placeholder: 'Insert a value',
          },
          minutes: {
            label: 'Minutes',
            placeholder: 'Insert minutes',
          },
          percentage: {
            label: 'Percentage',
            placeholder: 'Insert a percentage',
          },
          mealTime: {
            label: 'Meal time',
            placeholder: 'Select a schedule',
            options: {
              'before-meal': 'Antes da refeição',
              'after-meal': 'Depois da refeição',
            },
          },
          bnp: {
            label: 'Natriuretic Peptide',
            placeholder: 'Insert a value',
          },
          nt_proBnp: {
            label: 'N-terminal (NT)-pro BNP',
            placeholder: 'Insert a value',
          },
          type: {
            label: 'Type',
            placeholder: 'Select a type',
            options: {
              'in-bed': 'In bed',
              awake: 'Awake',
              rem: 'REM',
              core: 'Core',
              deep: 'Deep',
            },
          },
          startTime: {
            label: 'Start time',
          },
          endTime: {
            label: 'End time',
          },
        },
        units: {
          steps: 'steps',
        },
        required_message: 'Required',
        number_pattern_validation: {
          min: 'Min. {{ min_suffix }}',
          max: 'Max. {{ max_suffix }}',
        },
      },
      undo_task: 'Undo task',
      undo: 'Undo',
      edit_entry: 'Edit record',
      confirm: 'Confirm',
      snackbar: {
        update: {
          success: 'The task was successfully registered!',
          error: 'An error occurred when updating the task log.',
        },
        reset: {
          success: 'The task has returned to its initial state!',
          error: 'An error occurred when resetting the task log.',
        },
      },
      history: {
        title: 'History',
        update: 'edited the task',
        status: 'Status',
        someone: 'Someone',
        remove: 'undid the task',
        auto: 'The task was considered as completed by the system',
        validate: 'validate the task',
        create: 'created the task',
        read: 'read the task',
        contract_update: 'updated the contract',
        changes: 'Changes',
        task_not_done_reason: 'Task not done reason',
        notes: 'Notes',
        files_added: 'Files added',
        files_removed: 'Files removed',
      },
      files: 'Files',
    },
  },
  configurations_fields: {
    feature: {
      name: {
        placeholder: 'Write a name for the task',
      },
    },
  },
  schedule_modal: {
    trigger_button_label: 'Repetition interval',
    activity: {
      title: 'Choose Period - Customized',
    },
    task: {
      title: 'Choose Period - Customized',
    },
  },
  schedule_field: {
    readable_periodicity: {
      not_defined: 'Not defined',
      no_repetition: '(No repetition)',
      defined_per_activity: 'Repetition defined by activity',
      at_every: 'At every',
      hourly: 'hour',
      daily: 'day',
      weekly: 'week',
      monthly: 'month',
      weekly_at: 'at',
      and: 'and',
      at_hour: 'at',
      until_end_day: 'until',
    },
    quantities: 'Quantities',
  },
  activity_type_posology_schedule_form: {
    title: 'Define Posology',
    fields: {
      psology: {
        label: 'Posology',
      },
      posology_schedule_type: {
        label: 'Posology',
        options: {
          [PosologySchedule.PeriodsOfTheDay]: 'Period of the day',
          [PosologySchedule.TimesList]: 'Timetable',
          [PosologySchedule.Repetition]: 'Hourly',
          [PosologySchedule.SOS]: 'SOS',
        },
      },
      frequency: {
        options: {
          [Frequency.Hourly]: 'Hours',
          [Frequency.Daily]: 'Days',
          [Frequency.Weekly]: 'Weeks',
          [Frequency.Monthly]: 'Months',
        },
      },
      frequency_value: {
        label: 'Repeat every',
        validations: {
          min: 'The value must be equal to or greater than one.',
        },
      },
      time_list: {
        hour: {
          label: 'Hour',
        },
        quantity: {
          label: 'Quantity',
        },
        add: 'Add hour',
        required: 'Add at least one timetable.',
        error: 'Time already registered.',
      },
      periods_of_the_day_item: {
        required: 'Add at least one.',
      },
      repetitionHourlyFrequencyValue: 'Take every',
      repetitionHourlyFrequencyValueHour: 'hour',
      repetitionHourlyFrequencyValueHours: 'hours',
      repetition_quantity: {
        label: 'Quantities',
        validations: {
          min: 'The value must be equal to or greater than one.',
        },
      },
      end_date: {
        label: 'End Date',
        validations: {
          min: 'Invalid date',
        },
      },
      end_hour: {
        label: 'End Hour',
        validations: {
          min: 'Invalid hour',
        },
      },
    },
  },
  care_plan_sheet_activities_group_entry_configurationvalue: {
    repetition: {
      per_activity: 'Per activity',
      per_task: 'Per task',
    },
    aivd: {
      at_time: 'at',
      transportation: 'Transportation',
    },
  },
  periodicity_form: {
    empty_state: 'No repeat period has been defined yet.',
    fields: {
      frequency: {
        label: 'Repeat every:',
        hourly: 'Hours',
        daily: 'Days',
        weekly: 'Weeks',
        monthly: 'Months',
      },
      frequency_value: {
        label: 'Repetition per:',
        activity: 'Activity',
        task: 'Task',
        validations: {
          required: 'Required field',
          min: 'Frequency value has to be superior to one.',
          max: 'Frequency has to be inferior or equal to {{ value }}.',
        },
      },
      start_at_date: {
        label: 'Start date',
        validations: {
          superior_to_end_date: 'A data de inicío tem de ser anterior à data de fim.',
        },
      },
      end_date: {
        label: 'End date',
        validations: {
          superior_to_start_date: 'End date has to be posterior to the start date.',
        },
      },
      end_hour: {
        label: 'End time',
      },
      days: {
        label: 'Days of the week',
        validations: {
          required: 'Required field',
        },
      },
    },
  },
  role_permissions_professional_caregiver_office_screen: {
    generic_header: {
      subtitle: 'Create positions and manage permissions',
    },
    fields: {
      name: {label: 'Name', placeholder: 'Add a name to the position'},
      description: {label: 'Description', placeholder: 'Add a description'},
    },
    view: {
      tooltip: 'Allows the user to see content',
      label: 'View',
    },
    edit: {
      tooltip: 'Allows the user to edit content',
      label: 'Edit',
    },
    select_all_permissions: 'Select all permissions',
    localized_string_input: {
      name: {
        title: 'Translations for the name',
      },
      description: {
        title: 'Translations for the description',
      },
    },
    router_prompt: {
      title: 'Positions and permissions',
      content: {
        title: 'Are you sure you want to leave?',
        subtitle: 'Any changes made will be lost.',
      },
    },
    snackbar: {
      load: {
        error: 'An error has occurred while loading the page.',
      },
      update: {
        success: 'Successfully updated!',
        error: 'An error occurred when updating the permissions for this position.',
      },
      exit: 'The changes have not been saved.',
    },
  },
  shifts_page: {
    shifts: 'Shifts',
    team: 'Team',
    create_button: 'Create',
    schedule: 'Schedule:',
    to_hour: 'to',
    responsible: {
      label: 'Responsible',
      not_defined: 'Not defined',
    },
    professionals: {
      singular: 'Professional',
      plural: 'Professionals',
    },
    teams: {
      singular: 'team',
      plural: 'teams',
    },
    empty_state: {
      title: 'No shifts registered yet.',
      subtitle: 'Click create to register a new shift.',
    },
    shift_modal: {
      create_shift: 'Create shift',
      edit_shift: 'Edit shift',
      shift_form: {
        name: 'Shift name',
        name_placeholder: 'Morning, Afternoon, Evening shift',
        required_field_message: 'Mandatory field.',
        min_required_field_message: 'Mandatory.',
        pattern_field_message: 'Invalid field.',
        max_characters_name_message: 'Max {{ characters }} characters.',
        abbreviation: 'Id',
        abbreviation_placeholder: 'M(morning), A(afternoon), E(evening)',
        color: 'Color',
        social_response: 'Shift care services',
        social_responses_placeholder_input: 'Insert care services',
        people_per_shift: 'People on shift',
        people_per_shift_placeholder_input: 'Maximum number of people per shift',
        start_hour: 'Start hour',
        end_hour: 'End hour',
        shift_responsible: 'Shift responsible',
        shift_responsible_placeholder: 'No responsible person assigned',
        snackbar: {
          create_shift: {
            success: 'Shift {{ name }} successfully created!',
            error: 'Error creating the shift.',
          },
          update_shift: {
            success: 'Shift {{ name }} successfully updated!',
            error: 'Error updating the shift.',
          },
        },
      },
    },
    confirm_delete: {
      title: 'Delete shift - {{ name }}',
      copy: {
        title: 'Are you sure you want to delete this shift?',
        subtitle: 'This action cannot be undone.',
      },
    },
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    delete: 'Delete',
  },
  planner: {
    header: {
      title: 'Calendar',
      filter_all_seniors_label: 'All users',
      filter_input_placeholder: 'Filters',
      range: {
        day: 'Day',
        week: 'Week',
        month: 'Month',
      },
      add: 'Add',
      add_disabled: 'You do not have permission to add this task.',
      toggler: {
        calendar: 'calendar',
        list: 'list',
      },
    },
    body: {
      untreaten: '',
      untreaten_one: '{{count}} Not treated',
      untreaten_other: '{{count}} Not treated',
      empty: 'No tasks to display.',
    },
    side: {
      see_all: 'See all',
      birthdays: 'Birthdays',
    },
    modal: {
      create: {
        activity: {
          ativities_group: 'Activity',
          ativities_group_placeholder: 'Select an activity',
          activities_type: 'Task',
          activities_type_placeholder: 'Select a task',
          add_users_label: 'Add users',
          add_users_placeholder: 'Add users',
          added_users: 'Added users',
          add_users_type: {
            social_response: 'Care services',
            user: 'Users',
          },
          add_users_mark: '',
          add_users_mark_one: 'Mark all {{sr}} ({{count}} user)',
          add_users_mark_other: 'Mark all {{sr}} ({{count}} users)',
          add_users_mark_all: 'Select all users',
          success: '',
          success_one: 'The task was successfully added!',
          success_other: 'The tasks were successfully added!',
        },
      },
      delete: {
        title: {
          single: 'Are you sure you want to delete the task for the user "{{name}}"?',
          multiple: 'Are you sure you want to delete the task "{{name}}"?',
        },
        desc: {
          single: 'The task will be deleted in the care plan of the user.',
          multiple: 'The tasks will be deleted in the care plan of each user.',
        },
      },
    },
    item_menu: {
      activity: {
        users: 'Users',
        action: {
          delete: 'Delete',
          delete_disabled: 'You do not have permission to delete this task.',
          edit: 'Edit Plan',
          plan: 'Plan',
          register: 'Regist',
        },
      },
      anniversary: {
        birthday: 'Birthday',
        born: 'Born in',
        years: 'years old',
      },
    },
    errors: {
      content: 'An error occurred while loading the calendar.',
      save_activity: 'An error occurred while saving the activity. Please try again.',
      delete_activity: 'An error occurred while deleting the task(s). Please try again.',
      unknown_item_type: "Unknown planner item type: '{{ type }}'",
    },
  },
  care_services: {
    header: {
      add_button_disabled_tooltip: 'You do not have permissions to add services',
      seniors_label: 'Seniors',
      filter_caregivers_label: 'All professionals',
    },
    form: {
      required_field_message: 'This field is required',
      title_label: 'Title',
      title_placeholder: 'Service title',
      color_label: 'Color',
      start_date_label: 'Start date',
      end_date_label: 'End date',
      start_time_label: 'Start time',
      end_time_label: 'End time',
      repetition_label: 'Recurrence',
      responsible_users_label: 'Associated professionals',
      responsible_users_placeholder: 'Select a responsible',
      senior_label: 'Senior',
      senior_placeholder: 'Select senior',
      notes_label: 'Observations',
      notes_placeholder: 'Write an observation',
      permissions_disabled_tooltip: 'You do not have permission to edit this field',
      twenty_four_hours_label: 'Service 24 hours',
    },
    modals: {
      edit: {
        title: 'Edit service',
      },
      create: {
        title: 'Create service',
      },
      delete: {
        title: 'Delete service',
        description: 'Are you sure you want to delete this service?',
        cannot_be_undone: 'This action cannot be undone.',
      },
      repetition_options: {
        only_this_event: 'Only this event',
        this_and_following: 'This and following',
      },
      edit_recurrent: {
        title: 'Edit recurring service',
      },
      delete_recurrent: {
        title: 'Delete recurring service',
      },
      edit_confirmation: {
        title: 'Save changes to service',
        subtitle: 'Are you sure you want to edit the service?',
      },
    },
    toasts: {
      edit: {
        success: {
          title: 'Service updated',
          message: 'The service was updated successfully.',
        },
        error: {
          unknown: {
            title: 'Error updating service',
            message: 'An error occurred while updating the service. Please try again later.',
          },
          not_found: {
            title: 'Service not found',
            message: 'The care service you are trying to edit was not found.',
          },
          access_denied: {
            title: 'Access denied',
            message: 'You do not have permission to edit this care service.',
          },
          invalid_data: {
            title: 'Invalid data',
            message: 'The provided data is invalid. Please check the information and try again.',
          },
          end_date_invalid: {
            title: 'Invalid end date',
            message: 'The end date must be after the start date.',
          },
          repetition_required: {
            title: 'Edit error',
            message: 'Repetition is required when the service is recurring.',
          },
          repetition_removal_scope: {
            title: 'Edit error',
            message: 'To remove the repetition you need to edit all events in the series.',
          },
          repetition_change_scope: {
            title: 'Edit error',
            message: 'To change the repetition you need to edit all events in the series.',
          },
        },
      },
      create: {
        success: {
          title: 'Service created',
          message: 'The service was created successfully.',
        },
        error: {
          title: 'Error creating new service',
          message: 'An error occurred while creating the new service. Please try again later.',
        },
      },
      delete: {
        error: {
          title: 'Error deleting service',
          message: 'An error occurred while deleting the service. Please try again later.',
        },
      },
    },
    export: {
      repetition: {
        ends_on: 'Ends on',
        ends_after: 'Ends after',
        occurrences: 'occurrences',
      },
    },
    permissions: {
      view: 'You do not have permission to view services.',
    },
  },
  shared: {
    handle_care_task_modal: {
      confirm_edit: {
        title: 'Are you sure you want to edit the task?',
      },
      confirm_edit_cancel: {
        title: "You haven't saved the information yet. Are you sure you want to leave?",
        subtitle: 'Unsaved data will be lost.',
      },
      confirm_reset: {
        title: 'Are you sure you want to undo the task?',
        subtitle: 'The task will return to its initial state',
      },
    },
    input_disabled_tooltip: 'You do not have permissions to edit this field',
  },
  export: {
    plan: 'Plan',
    address: 'Address',
    contributor_number: 'Contributor No.',
    sns_number: 'Patient No.',
    contact: 'Contact',
    others: 'Others',
    details: 'Details',
    tasks: 'Tasks',
    task: 'Task',
    register: 'Record',
    date: 'Date',
    start_date: 'Start',
    end_date: 'End',
    periodicity: 'Periodicity',
    observations: 'Observations',
    no_records: 'No records',
    no_repetition: '(No repetition)',
    senior_profile: {
      save_pdf: 'Monitoring record (.pdf)',
      report_title: 'Monitoring record',
      user_section_title: 'Senior data',
      profile_section_subtitle: 'Profile',
      full_name: 'Full name',
      preferred_name: 'Preferred name',
      birth_date: 'Date of birth',
      sns_number: 'National Health Service Number',
      social_security_number: 'Social Security Number',
      taxpayer_number: 'Taxpayer number',
      additional_information: 'Additional information',
      health_conditions_subtitle: 'Conditions / Limitations',
      food_intolerances_title: 'Allergies / Intolerances',
      food_intolerances_subtitle: 'Intolerances (Food)',
      medication_allergies_subtitle: 'Allergies (Medication)',
      medication_title: 'Medication',
      latest_vital_signs_title: 'Vital Signs History (Last 7 Days)',
    },
    error: {
      pdf_generation: 'An error occurred while generating the PDF. Please try again later.',
      xlsx_generation: 'An error occurred while generating the Excel file. Please try again later.',
      pdf_monthly_not_supported: 'Monthly export is not supported.',
    },
  },
};
